import type { LocaleResources } from '../types';

export const en = {
  common: {
    matrixEditLabel: 'Change display name',
    matrixRestoreLabel: 'Restore catalog name',
    matrixPersonalLabelHint:
      'Only for your account, shared by Sales and Shipments. Does not change the catalog.',
    matrixLabelSaveFailed: 'Name was not saved. Refresh the data and try again.',
    matrixDisplayLabel: 'Your Product name',
    matrixLabelLimit: '1 to 80 characters, plain text.',
    matrixLabelPreview: 'Preview',
    appName: 'KJÖTKOMPANIÐ Operations',
    loading: 'Loading…',
    readFromImage: 'Read from image',
    importOrderFile: 'Import order file',
    fileImportReview: 'File import review',
    pdfTextUnavailable: 'This PDF does not contain text for automatic import.',
    storeMatches: 'STORE MATCHES',
    possibleStoreMismatch: 'POSSIBLE STORE MISMATCH: detected {{store}}.',
    selectedStore: 'Selected Store: {{store}}',
    imageReview: 'Image extraction review',
    imageReadFailed: 'The image could not be read. You can enter the order manually.',
    fileImportFailed: 'The file could not be read. You can enter the order manually.',
    reviewRequired: 'Review required',
    unit: 'Unit',
    addToDraft: 'Add to order draft',
    sources: 'Sources',
    save: 'Save',
    cancel: 'Cancel',
    language: 'Language',
    appearance: 'Appearance',
    appearanceLight: 'Light',
    appearanceDark: 'Dark',
    appearanceSystem: 'System',
    signedInAs: 'Signed in as {{name}}',
    foundationReady: 'Foundation is ready',
    foundationDescription: 'Retail operation modules will be delivered in later approved stages.',
    unavailable: 'Not available yet',
    quantityUnit: { PIECE: 'pcs', KG: 'kg' },
    errors: {
      MASTER_DATA_LOCK_TIMEOUT:
        'Another operation is updating Master Data. Refresh the data and try again.',
      technicalId: 'Technical ID: {{id}}',
    },
    incompleteQuantity: 'Some report days are missing; the value includes confirmed days only.',
    fractionalPieceQuantity: 'A fractional piece quantity requires review.',
  },
  auth: {
    title: 'Secure sign in',
    email: 'Email',
    code: 'One-time code',
    requestCode: 'Send code',
    verifyCode: 'Verify and sign in',
    signOut: 'Sign out',
    sent: 'If this account is active, a one-time code has been sent.',
    codeHint: 'The code expires in approximately 10 minutes.',
    invalid: 'The code could not be verified.',
    sessionExpired: 'Your session has expired. Sign in again.',
  },
  navigation: {
    open: 'Open navigation',
    close: 'Close navigation',
    overview: 'Master data',
    products: 'Products',
    abpCatalog: 'ABP Catalog',
    materials: 'Materials & ingredients',
    stores: 'Stores',
    networks: 'Networks',
    users: 'Users',
    settings: 'Settings',
    stock: 'Stock Status',
    sales: 'Sales',
    shipments: 'Shipments',
    warehouse: 'Warehouse',
    production: 'Production',
    forecast: 'Forecast',
    costing: 'Cost calculator',
    productCostLibrary: 'Product production cost',
    costInputBase: 'Cost input base',
    profitability: 'Profitability',
    orders: 'Store Orders',
    analytics: 'Analytics',
    dashboard: 'Dashboard',
    operations: 'Operations',
    importCenter: 'Import Center',
    overviewGroup: 'OVERVIEW',
    salesGroup: 'SALES',
    operationsGroup: 'OPERATIONS',
    economicsGroup: 'ECONOMICS',
    masterDataGroup: 'MASTER DATA',
    collapseSidebar: 'Collapse sidebar',
    expandSidebar: 'Expand sidebar',
  },
  users: {
    title: 'Users and access',
    role: 'Role',
    status: 'Status',
    active: 'Active',
    deactivated: 'Deactivated',
    managedByAdmin: 'User administration is protected by backend permissions.',
    createUser: 'Add user',
    create: 'Create user',
    cancel: 'Cancel',
    close: 'Close',
    email: 'Email',
    displayName: 'Display name',
    access: 'Access overrides',
    actions: 'Actions',
    manage: 'Manage',
    manageUser: 'Manage {{email}}',
    overrideCount_one: '{{count}} override',
    overrideCount_other: '{{count}} overrides',
    actionFailed: 'The access change could not be saved. Please try again.',
    activate: 'Activate',
    deactivate: 'Deactivate',
    confirmActivate: 'Activate this user?',
    confirmDeactivate: 'Deactivate this user and revoke active sessions?',
    revokeSessions: 'Revoke all sessions',
    effectiveAccess: 'Effective access',
    effectiveAccessDescription: 'Role defaults are shown together with any user-specific override.',
    permission: 'Permission',
    roleDefault: 'Role default',
    override: 'Override',
    effective: 'Effective',
    inherit: 'Inherit role',
    allow: 'Allow',
    deny: 'Deny',
    roles: {
      ADMIN: 'Admin',
      ELDHUS_MANAGER: 'Eldhús manager',
      SALESMAN: 'Salesman',
      VIEWER: 'Viewer',
    },
    roleDescriptions: {
      ADMIN: 'Full operational access; protected from role and status changes.',
      ELDHUS_MANAGER: 'Operational manager with warehouse and master-data access.',
      SALESMAN: 'Sales and commercial operations access.',
      VIEWER: 'Read-only operational access.',
    },
  },
  settings: {
    title: 'Personal settings',
    languageTitle: 'Interface language',
    languageDescription: 'This preference is stored securely with your user account.',
    appearanceDescription: 'Choose light, dark, or the current operating-system appearance.',
    saved: 'Language preference saved.',
  },
  validation: {
    required: 'This field is required.',
    invalidEmail: 'Enter a valid email address.',
    sixDigitCode: 'Enter the six-digit code.',
    generic: 'Check the entered values.',
  },
  master: {
    resultCount: '{{count}} results',
    previous: 'Previous',
    next: 'Next',
    viewSaved: 'View saved',
    saveView: 'Save view',
    search: 'Search',
    status: 'Status',
    showAll: 'Show all',
    sort: 'Sort',
    updated: 'Updated',
    notConfigured: 'Not configured',
    ready: 'Master data is ready',
    readyDescription:
      'Manage the canonical product, store and commercial configuration used by later operational stages.',
    productsCard: 'Products',
    productsCardDescription:
      'Catalog, units, mappings, distribution and effective commercial history.',
    storesCard: 'Stores',
    storesCardDescription:
      'Networks, locations, contacts, feedback, mappings and product activation.',
  },
  products: {
    title: 'Products',
    description: 'Canonical product catalog and effective commercial configuration.',
    create: 'Create product',
    sku: 'SKU',
    officialName: 'Official name',
    displayAlias: 'Display alias',
    category: 'Category',
    salesUnit: 'Sales unit',
    financialUnit: 'Financial unit',
    operationalArea: 'Operational area',
    netWeightKg: 'Net weight (kg)',
    piecesPerTray: 'Pieces per tray',
    name: 'Name',
    units: 'Units',
    distribution: 'Network distribution',
    salePriceNet: 'Sale price NET',
    productionCostNet: 'Production cost NET',
    back: 'Back to products',
    general: 'General',
    unitsSummary: 'Sales: {{sales}} · Financial: {{financial}} · Area: {{area}}',
    conversions: 'Product unit conversions',
    fromUnit: 'From unit',
    toUnit: 'To unit',
    factor: 'Factor',
    effectiveFrom: 'Effective from',
    add: 'Add',
    network: 'Network',
    mode: 'Mode',
    directStore: 'Direct store replenishment',
    centralSellThrough: 'Central warehouse sell-through',
    addVersion: 'Add version',
    centralExplanation:
      'Central sell-through does not imply store inventory, GOAL, reorder or stock alerts. Pipeline baseline posting is deferred to the inventory stage.',
    storeActivation: 'Store activation',
    store: 'Store',
    stores: 'Stores',
    salePriceHistory: 'Regular sale price history',
    netPrice: 'Net ISK unit price',
    costHistory: 'Production cost history',
    netCost: 'Net ISK unit cost',
    planningFoundation: 'GOAL, reorder and alert-threshold foundation',
    notCalculated:
      'No calculated values exist in this stage. Only explicit manual versions are stored, and only direct-store combinations are eligible.',
    manualValue: 'Manual value',
    setGoal: 'Set GOAL',
    setReorder: 'Set reorder override',
    setThreshold: 'Set threshold override',
    aliases: 'Source aliases',
    source: 'Source',
    sourceSku: 'Source SKU',
    sourceName: 'Source name',
    provenance: 'Mapping provenance',
    audit: 'Audit history',
    categoryCode: 'Category code',
    categoryName: 'Category name',
    manageCategories: 'Manage categories',
    createCategory: 'Create category',
    safetyBuffer: 'Safety buffer',
    activate: 'Activate',
    deactivate: 'Deactivate',
    shipmentSourceRoute: 'Shipment source',
    noShipmentSourceRoute: 'No assignment',
    routeReason: 'Route change reason',
    saveShipmentSourceRoute: 'Save source route',
    shipmentSourceMatch: 'Shipment source SKU match',
    shipmentSourceMatchHelp:
      'A manual match creates an authoritative alias for future Shipment imports.',
    matchShipmentSource: 'Match existing product',
    shipmentProductIntakeCount: 'New Shipment SKUs requiring review: {{count}}',
    shipmentProductIntakeTitle: 'Shipment product intake',
    shipmentProductIntakeReview: 'Needs review',
    shipmentProductIntakeSku: 'Source SKU',
    shipmentProductIntakeName: 'Source Product',
    shipmentProductIntakeStatus: 'Status',
    shipmentProductIntakeSuggestion: 'Suggested match / source',
    shipmentProductIntakeStatus_NEW: 'New',
    shipmentProductIntakeStatus_MATCH_REVIEW_REQUIRED: 'Match review required',
    shipmentProductIntakeStatus_UNASSIGNED: 'Needs source decision',
    shipmentProductIntakeStatus_AUTO_CREATED: 'Auto-created',
    shipmentProductIntakeStatus_RESOLVED: 'Resolved',
  },
  stores: {
    title: 'Stores',
    description: 'Canonical stores, contacts, source mappings and product activation.',
    create: 'Create store',
    network: 'Network',
    name: 'Store name',
    shortName: 'Short name',
    noPrimaryContact: 'No primary contact',
    activeProductsCount: '{{count}} active products',
    primaryContact: 'Primary contact',
    activeProducts: 'Active products',
    aliases: 'Source aliases',
    back: 'Back to stores',
    general: 'General',
    contacts: 'Contacts',
    primary: 'Primary',
    deactivate: 'Deactivate',
    activate: 'Activate',
    contactName: 'Contact name',
    roleTitle: 'Role / title',
    phone: 'Phone',
    email: 'Email',
    setPrimary: 'Set as primary',
    addContact: 'Add contact',
    feedback: 'Operational feedback',
    feedbackText: 'Feedback',
    optionalProduct: 'Optional product',
    categoryTag: 'Category / tag',
    addFeedback: 'Add feedback',
    product: 'Product',
    source: 'Source',
    rawAlias: 'Raw alias / code',
    provenance: 'Mapping provenance',
    addAlias: 'Add alias',
    extendHistory: 'Extend history',
    audit: 'Audit history',
    editContact: 'Edit',
    updateContact: 'Update contact',
  },
  networks: {
    title: 'Networks',
    description: 'Canonical retail networks shared by all users.',
    create: 'Create network',
    code: 'Code',
    name: 'Canonical name',
    shortName: 'Short name',
    edit: 'Edit network',
  },
  inventory: {
    title: 'Inventory',
    warehouseTitle: 'Warehouse inventory',
    description: 'Audited physical stock with explicit unknown state and reconstructable history.',
    positions: 'Inventory positions',
    openingStock: 'Opening stock',
    physicalCount: 'Physical count',
    physicalCountHelp:
      'Posts immutable counted stock and its variance against the observed ledger quantity.',
    warehouseCorrection: 'Controlled correction',
    warehouseCorrectionHelp:
      'Posts a separately auditable warehouse correction; it never edits a balance directly.',
    countHistory: 'Count and variance history',
    warehouseTransfer: 'Warehouse transfer',
    warehouseTransferHelp:
      'A transfer is an immutable movement between two previously initialized warehouse positions.',
    transferDestination: 'Destination warehouse',
    transferQuantity: 'Quantity to transfer',
    submitTransfer: 'Post transfer',
    transferPosted: 'Transfer posted. Source: {{from}}, destination: {{to}}.',
    history: 'Inventory history',
    pipeline: 'Network pipeline',
    locationKind: 'Location type',
    store: 'Store',
    warehouse: 'Warehouse',
    location: 'Location',
    allLocations: 'All locations',
    product: 'Product',
    unitFilter: 'Unit',
    allUnits: 'All units',
    current: 'Current quantity',
    actual: 'Actual physical quantity',
    quality: 'Data quality',
    stockStateLabel: 'Stock state',
    revision: 'Revision',
    unknown: 'UNKNOWN',
    enterActual: 'Enter physical count',
    physicalEntry: 'Physical count and opening stock',
    businessDate: 'Business date',
    reason: 'Reason',
    addOpeningLine: 'Add to opening draft',
    confirmOpening: 'Confirm opening stock',
    submitCorrection: 'Submit correction',
    submitPhysicalCount: 'Confirm physical count',
    warehouseCountPosted: '{{count}} physical count line(s) posted.',
    openingDraft: 'Opening stock draft',
    date: 'Date',
    movement: 'Movement',
    result: 'Result',
    variance: 'Variance',
    varianceHelp: 'Actual physical quantity minus the observed ledger quantity.',
    calculated: 'Calculated quantity',
    lastMovement: 'Last movement',
    lastPhysicalCount: 'Last physical count',
    actions: 'Actions',
    countAction: 'Count',
    correctAction: 'Correct',
    viewMovements: 'Movements',
    evidence: 'Evidence',
    pieceUnit: 'pcs',
    kilogramUnit: 'kg',
    estimatedPipeline: 'ESTIMATED NETWORK PIPELINE',
    pipelineExplanation: 'This is a verified baseline, not a live store-stock calculation.',
    pipelineRows: '{{count}} pipeline baselines',
    correctionPosted: 'Correction posted. Ledger delta: {{delta}}.',
    openingConfirmed: '{{count}} opening stock movements confirmed.',
    stockChanged:
      'Stock changed while this form was open. Review the refreshed quantity and explicitly submit again.',
    operationFailed: 'The inventory operation could not be completed.',
    stockWorkspaceTitle: 'Stock Status workspace',
    stockWorkspaceDescription: 'Read-only operational stock from the immutable inventory ledger.',
    asOf: 'As of',
    productSearch: 'Product search',
    matrixOrientation: 'Matrix orientation',
    storesByProducts: 'Stores × products',
    productsByStores: 'Products × stores',
    resetOrder: 'Reset order',
    storesCount: '{{count}} stores',
    productsCount: '{{count}} products',
    knownCells: '{{count}} known',
    incompleteCells: '{{count}} incomplete',
    notInitializedCells: '{{count}} not initialized',
    notManagedCells: '{{count}} centrally managed',
    lowCells: '{{count}} low',
    workspaceLoadFailed: 'Stock workspace could not be loaded.',
    stockMatrix: 'Stock matrix',
    rowLabel: 'Store / product',
    notManaged: 'CENTRAL',
    notInitialized: 'UNKNOWN',
    notApplicable: 'Not active for this store',
    stockDrillDown: 'Stock detail',
    goal: 'GOAL',
    difference: 'Difference',
    reorder: 'Reorder',
    provisional: 'provisional',
    distributionMode: 'Distribution mode',
    ledgerExplanation: 'Ledger explanation',
    stockState: {
      KNOWN: 'KNOWN',
      NOT_INITIALIZED: 'NOT INITIALIZED',
      INCOMPLETE_DATA: 'INCOMPLETE DATA',
      NOT_MANAGED: 'CENTRALLY MANAGED',
      NOT_APPLICABLE: 'NOT ACTIVE FOR THIS STORE',
    },
  },
  sales: {
    title: 'Sales workspace',
    description: 'Read-only operational view of confirmed daily Sales reports.',
    datePreset: 'Date range',
    from: 'From',
    to: 'To',
    network: 'Network',
    allNetworks: 'All networks',
    store: 'Store',
    allStores: 'All stores',
    productSearch: 'Product search',
    product: 'Product',
    allProducts: 'All products',
    orientation: 'Matrix orientation',
    storesByProducts: 'Stores × products',
    productsByStores: 'Products × stores',
    knownSales: 'Known sales',
    storesCount: '{{count}} stores',
    productsCount: '{{count}} active products',
    reportDays: '{{confirmed}} / {{expected}} confirmed report days',
    matrix: 'Sales matrix',
    rowLabel: 'Store / product',
    drillDown: 'Cell detail',
    date: 'Date',
    quantity: 'Quantity',
    coverageLabel: 'Coverage',
    known: 'Known',
    knownZero: 'Known zero',
    unknown: 'UNKNOWN',
    unknownCell: 'No data ⚠',
    sum: 'TOTAL',
    totalDrillDownHint: 'Show where and when sold',
    openTotalDrillDown: 'Show total for {{column}}',
    productSalesInPeriod: 'PRODUCT SALES IN PERIOD',
    storeSalesInPeriod: 'STORE SALES IN PERIOD',
    totalDetails: 'DETAILS',
    byStore: 'BY STORE',
    byDay: 'BY DAY',
    showZeros: 'Show zeros',
    hideZeros: 'Hide zeros',
    share: 'Share',
    storesWithSales: 'Stores with sales',
    incompleteTotals: 'INCOMPLETE DATA',
    incompleteTotalsDescription: 'Ranking and total include known confirmed quantities only.',
    loadFailed: 'Sales workspace could not be loaded.',
    coverage: { COMPLETE: 'COMPLETE', INCOMPLETE_DATA: 'INCOMPLETE DATA' },
    coverageDetailOpen: 'Open Sales coverage status',
    coverageDetailTitle: 'Data coverage status',
    coverageDetailGoToImportCenter: 'Go to Import Center',
    coverageDetailGroup: {
      missing: 'TO COMPLETE',
      ready: 'READY TO CONFIRM',
      needsFixing: 'NEEDS ATTENTION',
    },
    coverageDetailState: {
      NOT_UPLOADED: 'No report',
      PROCESSING: 'Processing',
      PREVIEW_READY: 'Ready to confirm',
      BLOCKED: 'Blocked',
      CONFLICT: 'Conflict',
      FAILED: 'Import failed',
    },
    importConflictsOpen: 'Open import conflicts',
    importConflictsCount: 'IMPORT CONFLICTS: {{count}}',
    importConflictsTitle: 'IMPORT CONFLICTS',
    importConflictExistingConfirmed: 'Existing report confirmed',
    compareConflict: 'Compare',
    importConflictAuthoritativeHint:
      'The confirmed report remains authoritative until an explicit decision is made.',
    quickPeriods: 'Quick periods',
    weekPeriods: 'Weeks',
    isoYear: 'Year',
    isoWeek: 'Week',
    isoWeekOption: 'Week {{week}} · {{range}}',
    notDue: 'Not due',
    activeRange: '{{from}} – {{to}}',
    expandFilters: 'Show filters',
    collapseFilters: 'Hide filters',
    columns: 'Columns',
    searchColumns: 'Search columns',
    showAllColumns: 'Show all',
    hideAllColumns: 'Hide all',
    allMatrixColumns: 'All columns',
    visibleColumnsCount: 'Visible: {{visible}} / {{total}}',
    columnSearchResults: 'Search results',
    showSearchResults: 'Show search results',
    hideSearchResults: 'Hide search results',
    noColumnSearchResults: 'No matching columns',
    noProductsSelected: 'No Products selected',
    noStoresSelected: 'No Stores selected',
    pinColumn: 'Pin at start',
    resetColumnLayout: 'Reset layout',
    resetRowOrder: 'Reset row order',
    pieceAbbreviation: 'pcs.',
    kgAbbreviation: 'kg',
    preset: {
      '7d': '7 days',
      '28d': '28 days',
      '90d': '90 days',
      ytd: 'YTD',
      last_confirmed_day: 'Last day',
      last_2_confirmed_days: 'Last 2 days',
      last_3_confirmed_days: 'Last 3 days',
      last_7_calendar_days: 'Last 7 days',
      last_completed_weekend: 'Last weekend',
      current_week: 'This week',
      previous_week: 'Previous week',
      iso_week: 'Choose week',
      custom: 'Custom range',
    },
  },
  salesImports: {
    title: 'Sales reports',
    description: 'Upload, validate, confirm and roll back official daily Sales Pivot workbooks.',
    upload: 'Upload workbook',
    history: 'Import history',
    filename: 'Filename',
    salesDate: 'Sales date',
    status: 'Status',
    statusValue: {
      UPLOADED: 'Uploaded',
      PROCESSING: 'Processing',
      PREVIEW_READY: 'Ready to confirm',
      BLOCKED: 'Blocked',
      DUPLICATE: 'Duplicate',
      DATE_CONFLICT: 'Data conflict',
      FAILED: 'Import failed',
      CONFIRMED: 'Confirmed',
      CANCELLED: 'Cancelled',
      ROLLED_BACK: 'Rolled back',
    },
    uploaded: 'Uploaded',
    stores: 'Stores',
    products: 'Products',
    nonzero: 'Non-zero facts',
    total: 'Total quantity',
    mappings: 'Mappings and validation',
    issues: 'Validation notices',
    canonicalIdentityConflict:
      'Several source identities map to one canonical Product, but they cannot be safely aggregated.',
    canonicalizedProductsNotice:
      'Several verified source identities have been aggregated into the canonical Product. Every source cell remains available for audit.',
    canonicalizedSourceCells: 'Canonicalized source cells',
    sourceCell: 'Source cell',
    sourceQuantity: 'Source quantity',
    nonRetailWarehouseDestination: 'Excluded — non-retail warehouse destination',
    storeIdentityMissing: 'Blocked — Store identity missing',
    distributionPolicyMissing: 'Distribution policy is missing for Product {{sku}} {{product}}',
    fixDistributionPolicy: 'Fix policy',
    resolveStore: 'Add or link Store',
    issueOccurrences: '{{count}} occurrences',
    sample: 'Parsed sample',
    confirm: 'Confirm official report',
    revalidate: 'Revalidate mappings',
    retry: 'Retry from stored source',
    rollback: 'Roll back report',
    rollbackReason: 'Mandatory rollback reason',
    detail: 'Report detail',
    duplicate: 'Duplicate — these data are already confirmed.',
    showExistingReport: 'Show existing report',
    conflict: 'The new source contains different data from the confirmed report for this date.',
    conflictComparisonTitle: 'Import conflict comparison',
    conflictComparisonDescription:
      'Compare the confirmed report with the new source. Changing data requires an explicit decision.',
    existingConfirmedReport: 'CURRENT REPORT — CONFIRMED',
    conflictingEvidence: 'NEW SOURCE — CONFLICT',
    conflictScope: 'Stores: {{stores}} · Products: {{products}}',
    conflictDifferences: 'Changed Store × Product positions',
    store: 'Store',
    product: 'Product',
    existingValue: 'Current',
    newValue: 'New value',
    difference: 'Difference',
    keepExistingReport: 'Keep existing report',
    replaceWithNewReport: 'Replace with new report',
    replaceConfirm:
      'Replace the confirmed Sales report with the new version? The previous report will remain as historical evidence and this day will be recalculated from the new report.',
    replaceBack: 'Back',
    keepConfirm:
      'Keep the currently confirmed report as the authoritative Sales truth. The new file remains immutable conflict evidence.',
    conflictReason: 'Decision reason',
    conflictReasonHelp:
      'Required for the audit trail. Describe why this report remains or is replaced.',
    conflictAdminReason:
      'Only an Administrator can make this audited decision. A reason is mandatory.',
    confirmConflictDecision: 'Confirm audited decision',
    conflictStepExplain: 'Conflict',
    conflictStepCompare: 'Compare',
    conflictStepDecision: 'Decision',
    conflictStepConfirm: 'Confirmation',
    conflictRows_one: '{{count}} non-zero row',
    conflictRows_other: '{{count}} non-zero rows',
    changeType: 'Change',
    changeTypeValue: { ADDED: 'Added', REMOVED: 'Removed', CHANGED: 'Changed' },
    conflictHelpTitle: 'How should I resolve a conflict?',
    conflictHelpBody:
      'Compare the source, uploader, time, scope, row counts and every changed Store × Product value. Keep the existing report when it remains authoritative. Replace it only when the new source is the corrected official report; the previous report remains in history.',
    conflictResolution: {
      KEEP_EXISTING: 'Conflict reviewed. The current confirmed report remains authoritative.',
      REPLACED: 'The new report version is confirmed. The previous version remains in history.',
    },
    completeZero: 'Complete report + absent fact means known zero. Missing report means unknown.',
    choose: 'Choose an .xlsx Sales Pivot workbook',
    back: 'Back to import history',
    download: 'Download original',
    operationFailed: 'The Sales import operation could not be completed.',
  },
  importCenter: {
    email: {
      source: 'E-mail',
      sourceFilter: 'Source',
      browserSource: 'Browser upload',
      title: 'E-mail attachment imports',
      settings: 'E-mail administration',
      safety:
        'E-mail receipt stages and validates attachments only. Confirmation stays manual; no Sales, Shipment, inventory or pipeline facts are created automatically.',
      configured:
        'Application configuration is complete. DNS and live receiving must be verified separately before enabling.',
      notConfigured:
        'E-mail receiving is not configured. Addresses are not active. Follow the STAGING activation runbook.',
      disabled: 'E-mail receiving is disabled.',
      enabled: 'Enable e-mail attachment imports',
      addressMissing: 'Address not configured',
      addressActive: 'Enabled in application configuration',
      addressInactive: 'Inactive — do not send reports here',
      copyAddress: 'Copy {{type}} address',
      copied: 'Address copied.',
      copyFailed: 'Could not copy. Select the active address manually.',
      authenticationPolicy:
        'Automatic import requires an allowed sender/report type and SPF, DKIM and DMARC pass from Resend. Only jakub@kjotkompani.is may use the approved DKIM gray exception with SPF and DMARC pass; DKIM fail or unknown is never accepted. Other uncertain results stay quarantined. Report confirmation remains manual.',
      autoReleaseEnabled: 'Automatically import authenticated messages from allowed senders',
      providerVerified:
        'Automatically released under the approved sender-authentication policy using Resend verdicts. Report confirmation remains manual.',
      senders: 'Allowed senders and report types',
      senderAddress: 'Sender e-mail',
      addSender: 'Add sender',
      removeSender: 'Remove sender',
      limits:
        'Limits: {{attachments}} attachments; {{fileBytes}} bytes per file; {{totalBytes}} bytes per message.',
      reason: 'Audit reason',
      reasonHint: 'At least 10 characters. Do not include secrets or message contents.',
      actionFailed:
        'Action was not accepted. Check configuration, sender permissions, current state and retry limits, then refresh.',
      settingsFailed: 'E-mail configuration could not be loaded.',
      cancel: 'Cancel',
      save: 'Save settings',
      proceed: 'Proceed',
      refresh: 'Refresh',
      empty: 'No received e-mail sources in this filter.',
      typeUnresolved: 'Report type unresolved',
      counts:
        'Attachments {{total}} · processed {{processed}} · skipped {{skipped}} · failed {{failed}} · pending {{pending}}',
      adminApproved: 'Sender evidence reviewed and released by an Admin.',
      unverified: 'Sender authentication is unverified.',
      release: 'Review / release quarantine',
      retry: 'Retry unfinished attachments',
      attachments: 'Attachments and provenance ({{total}})',
      identifiers: 'Provider e-mail: {{provider}} · Message-ID: {{messageId}}',
      attachmentMeta: '{{size}} bytes · attempts {{attempts}}',
      unknown: 'Unknown',
      pendingHash: 'Not stored yet',
      provenance:
        'Provider attachment: {{attachment}} · import_file: {{file}} · bulk item: {{item}}',
      reviewReport: 'Review validation / report',
      releaseWarning:
        'Verify the sender independently before releasing. The sender and report type must remain allowed. This permits staging only, never confirmation.',
      retryWarning:
        'Only unfinished or failed work is retried, within the server attempt limit. Completed attachments and business evidence are not duplicated.',
      statusRECEIVED: 'Received',
      statusPROCESSING: 'Processing',
      statusQUARANTINED: 'Quarantined',
      statusREADY: 'Ready for manual confirmation',
      statusBLOCKED: 'Validation blocked',
      statusDUPLICATE: 'Duplicate',
      statusCOMPLETED_WITH_ERRORS: 'Completed with errors',
      statusFAILED: 'Failed',
      statusPENDING: 'Pending',
      statusSKIPPED: 'Skipped',
      statusCOMPLETED: 'Completed',
      statusCONFIRMED: 'Manually confirmed',
      statusCANCELLED: 'Cancelled',
      statusROLLED_BACK: 'Rolled back',
      pageSummary: 'Messages {{from}}–{{to}} of {{total}}',
      previousPage: 'Previous messages',
      nextPage: 'Next messages',
      reasonAuthentication: 'Verify the sender independently, then request Admin release.',
      reasonSender: 'Sender or report type is not allowed. Review the Admin allowlist.',
      reasonRecipient:
        'The recipient does not identify exactly one report type. Use a single configured receiving address.',
      reasonUnsupported: 'Unsupported attachment skipped; other attachments continue.',
      reasonLimit: 'A file, message or workbook safety limit was exceeded.',
      reasonWorkbook:
        'The attachment is not a valid supported XLSX workbook. Request a corrected source.',
      reasonUnsafe:
        'Encrypted, executable, formula or otherwise unsafe workbook content was rejected.',
      reasonValidation:
        'The existing report parser rejected this source. Review validation before retrying.',
      reasonTechnical:
        'Processing requires attention. Preserve the source and report this diagnostic code if retry fails.',
    },
    title: 'Import Center',
    description: 'Operational inbox for Sales and Shipment source evidence.',
    loading: 'Loading imports…',
    loadFailed: 'Import Center could not be loaded.',
    dateUnknown: 'Date pending',
    scopeUnknown: 'Scope pending validation',
    openBatch: 'Open batch',
    backToInbox: 'Back to inbox',
    salesBatch: 'Sales batch',
    shipmentBatch: 'Shipment batch',
    batchItems: 'Batch files',
    businessDates_one: '{{count}} business date',
    businessDates_other: '{{count}} business dates',
    confirmAllSafety: 'Only reports eligible under the existing report rules are confirmed.',
    confirmResult:
      'Confirmed {{confirmed}}; already confirmed {{already_confirmed}}; blocked {{blocked}}; failed {{failed}}.',
    tabs: {
      label: 'Import Center sections',
      actions: 'Requires action',
      missing: 'Missing reports',
      history: 'History',
      upload: 'Bulk upload',
    },
    filters: {
      from: 'From',
      to: 'To',
      type: 'Type',
      all: 'All',
      network: 'Network',
      store: 'Store',
      search: 'Filename, date or identifier',
      filename: 'Filename',
      reportDate: 'Report date',
      status: 'Status',
      sort: 'Sort',
      ascending: 'Ascending',
      descending: 'Descending',
      showTestData: 'Show test data',
    },
    columns: {
      report: 'Report',
      source: 'Source',
      scope: 'Scope',
      reason: 'Reason',
      counts: 'Rows',
      quantity: 'Quantity',
      action: 'Action',
      date: 'Date',
      type: 'Type',
      deadline: 'Expected by',
      state: 'State',
      uploader: 'Uploaded by',
      identifier: 'Identifier',
    },
    actions: {
      title: 'Reports requiring action',
      previousStatus: 'Previous report: {{status}}',
      rows: '{{count}} rows',
      valid: '{{count}} valid',
      errors: '{{count}} errors',
      unrecognized: '{{count}} unrecognized',
      emptyTitle: 'No action required',
      emptyBody: 'No import currently needs operational action.',
      retry: 'Retry',
      viewIssues: 'View issues',
      preview: 'Preview / confirm',
      openReport: 'Open report',
      processing: 'Processing',
      confirmAll: 'Confirm all ready',
      recalculateBatchStatus: 'Recalculate batch status',
      revalidate: 'Revalidate',
      revalidateBackfill: 'Revalidate dates',
    },
    statusReconciliation: {
      title: 'Recalculate batch status',
      loading: 'Checking authoritative domain state…',
      safety:
        'This operation only synchronizes the operational file status with an already confirmed report. It does not run parsing, validation or confirmation and does not change reports or facts.',
      current: 'Current: {{status}}',
      expected: 'After repair: {{status}}',
      eligible: 'To synchronize: {{count}}',
      excluded: 'Outside scope: {{count}}',
      currentState: 'File status',
      authoritativeState: 'Report status',
      scope: 'Repair scope',
      included: 'Included in repair',
      exclusionITEM_NOT_PREVIEW_READY: 'The file status is not eligible',
      exclusionDOMAIN_BATCH_MISSING: 'No linked domain report',
      exclusionDOMAIN_BATCH_NOT_CONFIRMED: 'The domain report is not confirmed',
      noDrift: 'This batch is already synchronized.',
      zeroEffects: 'Reports, facts, confirmation events, mappings and policies: 0 changes.',
      reason: 'Required reason',
      reasonHelp: 'The actor, time and exact before/after state are recorded in the audit trail.',
      confirmation: 'Type {{value}}',
      cancel: 'Cancel',
      apply: 'Recalculate status',
      saving: 'Saving…',
      previewFailed: 'The preview could not be prepared. Refresh the batch and try again.',
      failed: 'State changed or synchronization failed. Nothing was saved.',
      success: 'Synchronized {{count}} files. Batch status: {{status}}.',
    },
    conflicts: {
      title: 'Import conflicts',
      description: 'Compare new source evidence with active confirmed reports.',
      grouping: 'Conflict grouping',
      groupByReport: 'By report',
      groupBySourceUpload: 'By source upload',
      sourceCardLabel: 'Conflicts from file {{filename}}',
      sourceUploadedBy: 'Uploaded by: {{uploader}} · {{date}}',
      sourceDateRange: 'Date range: {{from}}–{{to}}',
      sourceConfirmed: 'Confirmed partitions: {{count}} · facts: {{facts}}',
      sourceConflicts: 'Conflicts to close: {{count}}',
      sourceConflictBreakdown: 'Differences: {{material}} · blocked: {{blocked}}',
      sourceOtherBlocked: 'Other blocked partitions: {{count}}',
      sourceKeepEffect:
        'Keeps the official reports. It does not change confirmed data or the other blocked partitions from this upload.',
      closeSourceConflicts: 'Keep official reports and close {{count}} conflicts from this upload',
      sourceEmptyTitle: 'No uploads with open conflicts',
      sourceEmptyBody: 'No source upload contains conflicts that need to be closed.',
      sourceKeepTitle: 'Keep official reports for the source upload',
      sourcePreservedEvidence:
        'The file, source data, issues, and existing confirmed reports remain preserved.',
      sourceEvidenceDifferences:
        'Differences in the source being closed — informational only; they will not be saved as facts',
      sourceExpectedEffects:
        'Operation effect: Sales reports {{reports}}, Sales facts {{facts}}, confirmations {{confirmations}}, mappings {{mappings}}, policies {{policies}}, revalidations {{revalidations}}.',
      total: 'Total: {{count}}',
      materialCount: 'Business differences: {{count}}',
      blockedCount: 'Blocked: {{count}}',
      classification: 'Classification',
      classificationEXACT_DUPLICATE: 'Exact duplicate',
      classificationMETADATA_ONLY: 'Metadata only',
      classificationMATERIAL_DIFFERENCE: 'Business difference',
      classificationBLOCKED: 'Blocked',
      existingReport: 'Existing report',
      newReport: 'New report',
      changed: 'Changed',
      added: 'Added',
      removed: 'Removed',
      negatives: 'Negative',
      recommendation: 'Recommendation',
      recommendationKEEP_EXISTING: 'Keep existing',
      recommendationCOMPARE: 'Compare and decide',
      recommendationRESOLVE_BLOCKERS: 'Resolve blockers first',
      compare: 'Compare',
      openFullComparison: 'Full comparison',
      factCount: '{{count}} positions',
      selectPage: 'Select current page',
      clearPage: 'Clear current page',
      selectAllResults: 'Select all results',
      clearAll: 'Clear all',
      selectedCount: 'Selected: {{count}}',
      selectReport: 'Select report dated {{date}}',
      allFilteredSelected: 'All non-blocked results in the current filter are selected.',
      keepExisting: 'Keep existing',
      replaceWithNew: 'Replace with new',
      keepTitle: 'Keep existing reports',
      replaceTitle: 'Replace active reports with new reports',
      previewCounts: 'Eligible: {{eligible}} · excluded: {{excluded}}',
      excludedReports: 'Excluded reports and reasons',
      reason: 'Required shared reason',
      confirmationText: 'Enter {{value}}',
      acceptNegatives: 'I accept {{count}} negative corrections in the new reports',
      cancel: 'Cancel',
      execute: 'Execute',
      executing: 'Executing…',
      operationFailed: 'The operation could not be prepared or executed. Refresh the comparison.',
      retryIncomplete: 'Retry incomplete',
      partitionResults: 'Partition results ({{count}})',
      partitionResultsLabel: 'Operation results for source partitions',
      partitionResult: 'Result',
      none: 'None',
      operationStatus:
        '{{status}} · completed {{completed}} · skipped {{skipped}} · failed {{failed}}',
      operationStatusPENDING: 'Pending',
      operationStatusRUNNING: 'Running',
      operationStatusPARTIAL: 'Partially completed',
      operationStatusCOMPLETED: 'Completed',
      operationStatusSKIPPED: 'Skipped',
      operationStatusFAILED: 'Failed',
      guardTEST_FIXTURE: 'Test data is excluded',
      guardNOT_MATERIAL_DIFFERENCE: 'The report has no business difference',
      guardUNIT_MISMATCH: 'Report units do not match',
      guardUNKNOWN_UNIT: 'The report contains an unsupported unit',
      guardACTIVE_REPORT_COUNT_CHANGED: 'The active report count has changed',
      guardPARALLEL_CONFLICT: 'More than one conflict exists for this date',
      guardSEMANTIC_HASH_MISSING: 'The complete report data fingerprint is missing',
      guardSOURCE_EVIDENCE_MISSING: 'The immutable source-upload link is missing',
      guardSOURCE_EVIDENCE_MISMATCH: 'The immutable source-upload evidence no longer matches',
      guardAUTHORITATIVE_REPORT_NOT_CONFIRMED:
        'The report selected as authoritative is no longer confirmed',
      guardIMPORT_CONFLICT_STATE_CHANGED:
        'The report or source-evidence state changed after the preview',
      guardIMPORT_CONFLICT_OPERATION_FAILED:
        'This partition could not be closed; unfinished items can be retried safely',
      guardSHIPMENT_RECONCILIATION_REQUIRED: 'Shipment reconciliation must be completed',
      guardSHIPMENT_PARTIAL_RANGE_REPLACE_UNSAFE:
        'A shorter source cannot replace the entire confirmed Shipment report',
      emptyTitle: 'No open conflicts',
      emptyBody: 'No report requires comparison with a confirmed report.',
    },
    incompleteSource: {
      title: 'Reject incomplete source partitions',
      selectedCount: 'Selected incomplete partitions: {{count}}',
      selectEligible: 'Select eligible on this page',
      clearSelection: 'Clear selection',
      previewAction: 'Preview rejection',
      preparing: 'Preparing preview…',
      previewFailed:
        'The rejection preview could not be prepared. Refresh the inbox and try again.',
      selectPartition: 'Select incomplete partition from {{filename}}',
      select: 'Select for rejection',
      previewCounts: 'Selected: {{selected}} · eligible: {{eligible}} · excluded: {{excluded}}',
      eligible: 'Eligible',
      excluded: 'Excluded',
      blockers: 'Blocking evidence',
      unknownBlocker: 'Unresolved blocking issue',
      quantityTotals: 'Non-zero source quantities',
      entityCounts: 'Stores: {{stores}} · Products: {{products}}',
      sourceRows: 'Non-zero source cells: {{cells}} · source rows {{first}}–{{last}}',
      blankStoreEvidence: 'Blank Store evidence: {{count}} unmapped source column(s)',
      currentEffects:
        'Current business effects — reports: {{reports}} · facts: {{facts}} · active confirmed reports: {{confirmed}}',
      sharedSource:
        'Shared source — partitions: {{partitions}} · confirmed: {{confirmed}} · duplicates: {{duplicates}} · confirmed facts: {{facts}}',
      exclusionReasons: 'Cannot be rejected',
      notEligible: 'This partition is no longer eligible',
      effect:
        'Partitions changed to CANCELLED: {{count}}. Sales reports: {{reports}}; Sales facts: {{facts}}. No mapping, confirmation, revalidation, deletion, or shared-file change will occur. Coverage for each date remains MISSING (unknown), never zero.',
      reason: 'Required rejection reason',
      reasonHelp: 'The reason is retained with the immutable audit evidence.',
      confirmationText: 'Type exactly: {{value}}',
      confirmationHelp: 'The text must match the server preview exactly.',
      reject: 'Reject incomplete source',
      rejecting: 'Rejecting…',
      cancel: 'Cancel',
      close: 'Close',
      operationFailed:
        'The incomplete-source rejection could not be completed. Refresh the inbox and prepare a new preview.',
      operationId: 'Operation ID: {{operationId}}',
      replayed: 'Idempotent replay',
      result: '{{status}} · completed {{completed}} · skipped {{skipped}} · failed {{failed}}',
      resultsLabel: 'Incomplete-source rejection results',
      resultStatusCOMPLETED: 'Completed',
      resultStatusPARTIAL: 'Partially completed',
      resultStatusFAILED: 'Failed',
      outcomeStatusCOMPLETED: 'Cancelled',
      outcomeStatusSKIPPED: 'Skipped',
      outcomeStatusFAILED: 'Failed',
      none: 'None',
      partitionFailed: 'This partition could not be rejected',
      errorCodeIMPORT_INCOMPLETE_SOURCE_STATE_CHANGED: 'Source evidence changed after the preview',
      errorCodeIMPORT_INCOMPLETE_SOURCE_NOT_REJECTABLE: 'The partition is no longer eligible',
      errorCodeIMPORT_INCOMPLETE_SOURCE_PREVIEW_MISMATCH:
        'The preview no longer matches the request',
      errorCodeIMPORT_INCOMPLETE_SOURCE_CONFIRMATION_MISMATCH:
        'The confirmation text did not match',
      guardIMPORT_TYPE_NOT_SUPPORTED: 'Only Sales partitions are supported',
      guardSTATUS_NOT_BLOCKED: 'The partition is no longer blocked',
      guardSOURCE_PARTITION_MISSING: 'Immutable source-partition evidence is missing',
      guardALREADY_CONFIRMED: 'The partition was already confirmed',
      guardIS_DUPLICATE: 'The partition is a duplicate',
      guardIS_CONFLICT: 'The partition has an import conflict',
      guardHAS_BUSINESS_EFFECT: 'The partition already has a business effect',
      guardDATE_ALREADY_CONFIRMED: 'The date already has a confirmed report',
      guardIS_DUPLICATE_CANONICAL:
        'Another import refers to this partition as its duplicate source',
      guardHAS_CONFLICT_RESOLUTION: 'A conflict decision already exists for this partition',
      guardTEST_DATA_USES_SEPARATE_WORKFLOW: 'Test data uses the separate test-data workflow',
      guardNO_BLOCKING_ISSUE: 'No blocking issue remains',
      guardNO_EMPTY_UNKNOWN_STORE_EVIDENCE: 'No blank unknown-Store blocker was found',
      guardNO_EMPTY_UNMAPPED_STORE_EVIDENCE: 'No blank unmapped Store evidence was found',
      guardHAS_ADDITIONAL_BLOCKING_ISSUES:
        'The partition has additional blockers and requires a separate decision',
      guardHAS_NON_EMPTY_OR_MAPPED_STORE_EVIDENCE:
        'The partition contains a known or non-blank Store and cannot be rejected as a whole',
      guardNO_FRAGMENTARY_ROWS: 'No non-zero fragmentary source rows were found',
    },
    productIssues: {
      title: 'Common issues',
      description:
        'One mapping decision can safely unblock every unconfirmed report with the same source code.',
      sourceSku: 'Source code',
      sourceNames: 'Source name',
      affectedReports: 'Affected reports',
      reportCount_one: '{{count}} report',
      reportCount_other: '{{count}} reports',
      dateRange: 'Date range',
      occurrences: 'Occurrences',
      resolve: 'Resolve',
      openCommonIssue: 'Open common issue',
      adminRequired: 'Administrator required',
      resolveTitle: 'Resolve unknown Product {{sku}}',
      sourceAttributesUnavailable:
        'Sales Pivot does not provide an authoritative unit or net weight. Choose them explicitly when creating a Product; the system will not guess.',
      examples: 'Source examples',
      matchExisting: 'Match an existing Product',
      searchProducts: 'Search by SKU, name, or alias',
      decisionPreview: 'Decision and alias preview',
      canonicalAttributes: 'Unit: {{unit}} · Net weight: {{weight}}',
      notSet: 'not set',
      existingAliases_one: 'The Product has {{count}} existing alias.',
      existingAliases_other: 'The Product has {{count}} existing aliases.',
      reason: 'Decision reason',
      reasonHelp: 'The reason is retained in audit evidence and alias provenance.',
      resolveFailed: 'The decision could not be saved and the reports could not be revalidated.',
      technicalDetail: 'Technical detail: {{code}}',
      resultSummary:
        'Mapping saved. Total {{total}}, ready {{ready}}, still blocked {{blocked}}, revalidation failures {{failed}}.',
      previousAliases_one: '{{count}} previous alias was found for this source code.',
      previousAliases_other: '{{count}} previous aliases were found for this source code.',
      active: 'active',
      leaveUnresolved: 'Leave unresolved',
      createNew: 'Create new Product',
      resolving: 'Saving and revalidating…',
      retryFailed: 'Retry failed validations',
      retrying: 'Retrying validation…',
      matchAndRevalidate: 'Match and revalidate reports',
    },
    storeIssues: {
      title: 'New Stores and Products — Stores',
      description:
        'One explicit decision can link a source Store name or create a Store without guessing its Network.',
      sourceIdentity: 'Source Store name',
      type: 'Import type',
      affectedReports: 'Affected reports',
      dateRange: 'Date range',
      candidates: 'Possible existing Stores',
      noCandidates: 'No safe candidate',
      resolve: 'Add or link Store',
      resolveTitle: 'Resolve Store {{name}}',
      noAutomaticMatch:
        'The system will not switch the Store or Network automatically. Candidates are evidence for an explicit decision only.',
      reportCount_one: '{{count}} report',
      reportCount_other: '{{count}} reports',
      occurrenceCount_one: '{{count}} occurrence',
      occurrenceCount_other: '{{count}} occurrences',
      reports: 'Reports and dates',
      examples: 'Source examples',
      matchExisting: 'Link an existing Store',
      createNew: 'Add a new Store',
      searchStores: 'Search by Store or Network name',
      canonicalName: 'Canonical Store name',
      shortName: 'Short name (optional)',
      network: 'Network',
      networkRequired: 'The Network must be selected explicitly.',
      manageNetworks: 'Open Network catalog',
      activityDateSeparate:
        'First report occurrence sets the alias date; it is not evidence of the Store opening date.',
      effectiveFrom: 'Alias effective from',
      effectiveTo: 'Alias effective to (exclusive, optional)',
      aliasDateHelp:
        'This range applies only to source-name mapping. It does not change historical Store activity.',
      reason: 'Required decision reason',
      reasonHelp: 'Actor, decision, Store, Network, and alias are retained in audit.',
      resolveFailed: 'The decision could not be saved or reports could not be revalidated.',
      resultSummary:
        'Mapping saved. Total {{total}}, ready {{ready}}, still blocked {{blocked}}, failures {{failed}}.',
      leaveUnresolved: 'Leave unresolved',
      saveAndRevalidate: 'Save and check report',
      revalidating: 'Saving and revalidating…',
      retry: 'Resume failed validation',
      locationOnlyWarning:
        '{{store}} ({{network}}) has a similar location name, but is not automatically the same Store.',
      matchBasis_EXACT_CANONICAL: 'exact canonical name',
      matchBasis_EXACT_ALIAS: 'exact existing alias',
      matchBasis_LOCATION_TOKEN: 'similar location name — review required',
    },
    distributionIssues: {
      title: 'Missing distribution policy',
      summary: 'Missing distribution policy: {{products}} Products · {{reports}} reports',
      product: 'Product',
      sourceSku: 'Source code',
      sourceName: 'Source name',
      sourceNames: 'Source names',
      scope: 'Scope',
      dateRange: 'Report dates',
      counts: 'Occurrences',
      currentPolicies: 'Current policies',
      noPolicies: 'None',
      fixPolicy: 'Fix policy',
      resolveTitle: 'Set distribution policy',
      explanation:
        'Distribution policy is missing for Product {{sku}} {{product}} in {{network}}. Product presence in a report is not approval to create a policy.',
      reportCount_one: '{{count}} report',
      reportCount_other: '{{count}} reports',
      occurrenceCount_one: '{{count}} occurrence',
      occurrenceCount_other: '{{count}} occurrences',
      action: 'Resolution',
      actionCreate: 'Create a new policy',
      actionExtend: 'Extend a current policy',
      actionLink: 'Link an existing policy',
      existingPolicy: 'Existing policy',
      wholeNetwork: 'Whole Network',
      selectedStores: 'Selected Stores',
      affectedStores: 'Stores identified by reports',
      mode: 'Distribution model',
      modeDirect: 'Direct Store replenishment',
      modeCentral: 'Central warehouse sell-through',
      effectiveFrom: 'Effective from',
      effectiveTo: 'Effective to (exclusive)',
      reason: 'Required decision reason',
      reasonHelp: 'Actor, previous state, new state, and reason are retained in audit.',
      previewTitle: 'Reports to revalidate: {{count}}',
      resolveFailed: 'The policy could not be saved or reports could not be revalidated.',
      result:
        'Validation completed: total {{total}}, ready {{ready}}, still blocked {{blocked}}, failures {{failed}}.',
      leaveUnresolved: 'Leave unresolved',
      saveAndRevalidate: 'Save and revalidate reports',
      revalidating: 'Saving and revalidating…',
      retry: 'Resume failed validation',
      scopeKindNETWORK: 'whole Network',
      scopeKindSTORES: 'selected Stores',
    },
    missing: {
      title: 'Missing reports',
      daysLate_one: '{{count}} day late',
      daysLate_few: '{{count}} days late',
      daysLate_many: '{{count}} days late',
      daysLate_other: '{{count}} days late',
      hoursLate: '{{count}} h late',
      shipmentScheduleUnavailable:
        'Sales uses the authoritative daily schedule. No Shipment delivery schedule is configured, so Shipment gaps are not invented.',
      exportCsv: 'Export CSV',
      activeScope: '{{stores}} active Stores · {{networks}} Networks',
      dailySchedule: 'Daily Sales schedule',
      openImport: 'Open import',
      openCommonIssue: 'Fix common issue',
      upload: 'Upload report',
      emptyTitle: 'No missing Sales reports',
      emptyBody: 'Every expected Sales day in this range has an authoritative confirmed report.',
    },
    missingState: {
      EXPECTED_TODAY: 'Expected today',
      OVERDUE: 'Overdue',
      IN_PROGRESS: 'Import in progress',
    },
    history: {
      title: 'Import history',
      open: 'Open',
      markTestData: 'Mark as test data',
      markTestDataTitle: 'Mark historical import as test data',
      markingTestData: 'Marking…',
      testDataLabel: 'Test-data label',
      testDataLabelHelp: 'The label must start with “STAGING TEST”.',
      testDataReason: 'Reason',
      markerKeepsStatus:
        'This operation only adds an immutable test-data marker. Display status: {{displayStatus}}; domain status: {{domainStatus}}. Reports, facts and history will not change.',
      domainStatus: 'Domain status: {{status}}',
      dispositionIncompleteSourceRejected: 'Disposition: incomplete source rejected',
      rejectionReason: 'Reason: {{reason}}',
      markTestDataFailed: 'The import could not be marked as test data.',
      markTestDataSuccess: 'The import was marked as test data without changing its history.',
      cancel: 'Cancel',
      emptyTitle: 'No history',
      emptyBody: 'No imported report matches the selected filters.',
    },
    recommended: {
      COMPARE_AND_RESOLVE: 'Compare and resolve',
      RETRY_OR_REUPLOAD: 'Retry / inspect',
      FIX_MAPPINGS_AND_REVALIDATE: 'Fix and revalidate',
    },
    status: {
      QUEUED: 'Queued',
      UPLOADED: 'Uploaded',
      PARSING: 'Parsing',
      PROCESSING: 'Processing',
      PREVIEW_READY: 'Ready to confirm',
      READY: 'Ready',
      BLOCKED: 'Blocked',
      DUPLICATE: 'Duplicate',
      DATE_CONFLICT: 'Conflict',
      CONFLICT: 'Conflict',
      FAILED: 'Failed',
      CONFIRMED: 'Confirmed',
      COMPLETED: 'Completed',
      COMPLETED_WITH_ERRORS: 'Completed with issues',
      REVIEW_REQUIRED: 'Review required',
      CANCELLED: 'Cancelled',
      INCOMPLETE_SOURCE_REJECTED: 'Incomplete source rejected',
      REVALIDATION_FAILED: 'Revalidation failed',
      ROLLED_BACK: 'Rolled back',
      REJECTED: 'Rejected',
      SUPERSEDED: 'Superseded',
      OVERLAP: 'Overlap — existing report kept',
      NOT_UPLOADED: 'Not uploaded',
    },
    reason: {
      BULK_IMPORT_PROCESSING_FAILED: 'Processing failed',
      SHIPMENT_INVOICE_NUMBER_REQUIRED: 'The Shipment row is missing an invoice number.',
      SHIPMENT_INVOICE_NUMBER_UNSAFE_NUMERIC:
        'The invoice number was stored in Excel as an unsafe number and may have lost digits. Format the Invoice # column as Text and re-enter the original number.',
      SHIPMENT_INVOICE_NUMBER_INVALID:
        'The invoice number has an unsupported type or format. Format Invoice # as Text.',
      SHIPMENT_INVOICE_NUMBER_TOO_LONG: 'The invoice number exceeds the 128-character limit.',
      DATE_CONFLICT: 'Confirmed report contains different data',
      UNKNOWN_STORE: 'Unknown Store identity',
      AMBIGUOUS_STORE: 'Ambiguous Store identity',
      UNKNOWN_PRODUCT: 'Unknown Product identity',
      AMBIGUOUS_PRODUCT: 'Ambiguous Product identity',
      MISSING_PRODUCT_DISTRIBUTION: 'Product distribution is missing',
      DISTRIBUTION_POLICY_MISSING: 'Product distribution policy is missing',
      CENTRAL_RECONCILIATION_UNSUPPORTED: 'Central reconciliation is unsupported',
      BLOCKED: 'Validation is blocked',
      FAILED: 'Import failed',
      SHIPMENT_MANUAL_RECONCILIATION_REQUIRED:
        'Shipment requires manual reconciliation with an existing warehouse movement',
    },
    testData: 'Test data',
    summary: {
      total: 'Total',
      processing: 'Processing',
      ready: 'Ready',
      blocked: 'Blocked',
      duplicate: 'Duplicate',
      conflict: 'Conflict',
      overlapExistingConfirmed: 'Overlap kept',
      failed: 'Failed',
      confirmed: 'Confirmed',
      cancelled: 'Cancelled',
    },
    uploadDurableBatch:
      'The durable batch is saved. Open it or keep watching individual uploads here.',
    upload: {
      type: 'Import type',
      choose: 'Choose XLSX files',
      limits: 'Up to 100 files, 10 MB each and 100 MB per batch.',
      drop: 'Drop XLSX files here or choose files',
      progress:
        '{{processed}} / {{total}} processed · {{accepted}} accepted · {{failed}} failed · {{invalid}} invalid · {{ready}} ready · {{inProgress}} in progress · {{cancelled}} cancelled',
      technicalId: 'Technical ID: {{id}}',
      fileList: 'Selected import files',
      filename: 'Filename',
      size: 'Size',
      detectedType: 'Detected type',
      detectedDate: 'Detected date',
      state: 'State',
      actions: 'Actions',
      afterValidation: 'After validation',
      retry: 'Retry',
      remove: 'Remove',
      uploading: 'Uploading…',
      start: 'Upload {{count}} ready files',
      retryFailed: 'Retry failed files',
      cancelRemaining: 'Cancel remaining',
    },
    uploadState: {
      CHECKING: 'Checking',
      READY: 'Ready',
      INVALID: 'Invalid',
      UPLOADING: 'Uploading',
      QUEUED: 'Queued',
      DUPLICATE: 'Duplicate evidence',
      FAILED: 'Failed',
      CANCELLED: 'Cancelled',
    },
    errors: {
      invalidDate: 'Enter a valid date range. The end date cannot be before the start date.',
      BULK_IMPORT_FILE_COUNT_INVALID: 'The batch exceeds 100 files.',
      BULK_IMPORT_XLSX_REQUIRED: 'Only XLSX files are accepted.',
      BULK_IMPORT_FILE_SIZE_INVALID: 'File must be between 1 byte and 10 MB.',
      BULK_IMPORT_BATCH_SIZE_INVALID: 'The batch exceeds 100 MB.',
      DUPLICATE_SELECTED: 'The same file was already selected.',
      BULK_IMPORT_DUPLICATE_SOURCE: 'This source already exists in the batch.',
      UPLOAD_NETWORK_ERROR: 'Upload interrupted. Retry this file.',
      INTERNAL_ERROR:
        'The file could not be saved (code: INTERNAL_ERROR). Retry it; if the error repeats, provide the technical ID.',
      MASTER_DATA_LOCK_TIMEOUT:
        'Another operation is updating Master Data. Refresh the data and try again.',
      retryFailed: 'Retry was not accepted.',
      revalidateFailed: 'Revalidation failed.',
      confirmFailed: 'Confirm All Ready failed. No report was force-confirmed.',
    },
  },
  shipmentWorkspace: {
    physicalQuantityBasis:
      'Physical quantities include approved dispositions. Older reports without a disposition retain the original invoice quantity; this is not proof of physical movement.',
    sourceInvoiceQuantity: 'Source invoice quantity',
    title: 'Shipments',
    description: 'Confirmed actual Store shipments by Shipment date.',
    openImports: 'Shipment imports',
    activeRange: '{{from}} — {{to}}',
    confirmedOnly: 'Confirmed only',
    activeFilters: '{{count}} active filter(s)',
    expandFilters: 'Show filters',
    collapseFilters: 'Hide filters',
    datePreset: 'Period',
    weekPeriods: 'Week',
    preset: {
      '7d': '7 days',
      '28d': '28 days',
      '90d': '90 days',
      ytd: 'YTD',
      current_week: 'Current week',
      previous_week: 'Previous week',
    },
    isoYear: 'ISO year',
    isoWeek: 'ISO week',
    isoWeekOption: 'Week {{week}} · {{range}}',
    from: 'From',
    to: 'To',
    network: 'Network',
    allNetworks: 'All networks',
    store: 'Store',
    allStores: 'All Stores',
    productSearch: 'Search Products',
    product: 'Product',
    allProducts: 'All Products',
    orientation: 'Orientation',
    storesByProducts: 'Stores × Products',
    productsByStores: 'Products × Stores',
    totalShipped: 'Total shipped',
    storesCount: 'Stores: {{count}}',
    productsCount: 'Products: {{count}}',
    factsCount: 'Source lines: {{count}}',
    loadFailed: 'Shipment matrix could not be loaded.',
    columns: 'Columns',
    resetColumnLayout: 'Reset columns',
    resetRowOrder: 'Reset order',
    searchColumns: 'Search columns',
    showAllColumns: 'Show all',
    hideAllColumns: 'Hide all',
    allMatrixColumns: 'All columns',
    visibleColumnsCount: 'Visible: {{visible}} / {{total}}',
    columnSearchResults: 'Search results',
    showSearchResults: 'Show search results',
    hideSearchResults: 'Hide search results',
    noColumnSearchResults: 'No matching columns',
    noProductsSelected: 'No Products selected',
    noStoresSelected: 'No Stores selected',
    pinColumn: 'Pin column',
    matrix: 'Shipment matrix',
    rowLabel: 'Store / Product',
    sum: 'TOTAL',
    empty: 'No confirmed Store shipments in the selected period.',
    noShipment: 'No shipment',
    unit: { PIECE: 'pcs', KG: 'kg', TRAY: 'tray' },
    drillDown: 'Shipment details',
    byDay: 'By day',
    date: 'Shipment date',
    quantity: 'Quantity',
    sourceEvidence: 'Immutable source lines',
    source: 'Source',
  },
  shipmentImports: {
    approvedRepair: {
      title: 'Complete repair plan for this Shipment',
      explanation:
        'Administrator only. Preview reads the full batch and current catalog; apply runs one atomic Repair Plan but does not confirm the Shipment.',
      loading: 'Reading all lines and current master data…',
      drift:
        'The batch, routes, policies or product changed. Nothing was saved; prepare a new preview.',
      saved: 'Plan saved with audit evidence',
      scope:
        '80907: PIECE→KG; four routes; 80862/80865: MAIN→ELDHUS from 1 Oct; Prís policies; two invoice-only −528 corrections with no physical movement. Separate +180 deliveries are not linked or changed.',
      preview: 'Prepare fresh preview of the complete plan',
      issueResult: 'Issues: {{before}} before, {{after}} after the plan.',
      decisions:
        'Routes: {{routes}} · transitions: {{transitions}} · policies: {{policies}} · physical decisions: {{physical}} · unit change: 1',
      remainingNegatives: 'Remaining negative lines without decisions: {{count}}.',
      effectZero:
        'This action creates 0 reports, facts or movements; it does not confirm or KEEP/REPLACE.',
      noConfirmation:
        'This only repairs mappings and records decisions. The overlap and remaining negative lines may still block confirmation.',
      reason: 'Administrator reason',
      confirmationHint: 'Enter exactly',
      confirmation: 'Plan confirmation',
      close: 'Close',
      apply: 'Apply complete Repair Plan',
    },
    repairRoute: {
      title: 'Transition approved global Shipment routes',
      help: 'Admin-only audited Repair Plan. This transition applies only to SKUs 80862 and 80865: close the exact open MAIN routes and start ELDHUS on 2026-10-01. Both changes are atomic; no Shipment is confirmed.',
      failed:
        'The route transition could not be prepared or applied. No partial route change was committed.',
      saved:
        'The route transition was saved with audit and outbox evidence; the Shipment remains unconfirmed.',
      loading: 'Checking current route and warehouse revisions…',
      drift:
        'Current global routes or active ELDHUS warehouse differ from the approved state. No action is available.',
      revision: 'expected revision {{revision}}',
      reason: 'Owner-approved reason',
      preview: 'Preview atomic transition',
      previewSummary: 'Projected issues: {{before}} before, {{after}} after this route change.',
      confirmation: 'Exact confirmation',
      close: 'Close',
      apply: 'Apply Repair Plan',
      saving: 'Saving…',
    },
    incremental: {
      title: 'Incremental Shipment import',
      explanation:
        'Exact invoice-aware repeats are retained as source evidence and skipped. Only new deliveries can create facts and movements.',
      repeated: 'Repeated source rows: {{count}}',
      new: 'New deliveries: {{count}}',
      decision: 'Require a decision: {{count}}',
      blocked:
        'Changed or ambiguous rows require an explicit reconciliation; nothing will be silently replaced.',
      confirm: 'Skip repeats and add only new shipments',
      confirmationSummary:
        'Ready to confirm: {{added}} new rows; skipped repeats: {{repeated}}; changed: {{changed}}; approved corrections: {{corrections}}.',
      recipientCorrection: 'Recipient correction: {{totals}}',
      warehouseCorrection: 'Warehouse correction: {{totals}}',
      canonicalUnchanged: 'The existing confirmed report will not be replaced.',
    },
    physical: {
      stale:
        'Data changed since the disposition was approved. The previous version remains in history; a new preview and approval are required.',
      reapprove: 'Prepare a new disposition version',
      title: 'Physical movement disposition',
      help: 'Source quantities and invoices remain immutable. Decisions control future physical movements only; saving does not confirm the Shipment.',
      failed:
        'The operation failed. Check the scope and current batch state; no Shipment was automatically confirmed.',
      loading: 'Loading…',
      saved: 'Decisions saved with an audit trail. The Shipment was not automatically confirmed.',
      noNegative: 'No negative lines to classify.',
      source: 'Source / invoice net',
      kind: 'Event type',
      choose: 'Choose an event',
      original: 'Linked positive line',
      linkHelp: 'Select only an established link. Matching quantity does not prove a relationship.',
      priorFact: 'Confirmed fact from an earlier report (legacy correction)',
      priorFactHelp:
        'Use only an Admin-verified fact ID. It records report/transaction aggregate evidence, never a guessed historic movement.',
      unlinked:
        'Original invoice unlinked: suppress movements only for the selected negative line. Other positive deliveries remain unchanged.',
      reason: 'Decision reason',
      summary: 'Source versus physical effects',
      movementCount: 'Future ledger movements: {{count}}',
      unit: 'Unit',
      destinationNet: 'Recipient delta',
      warehouseNet: 'Source warehouse delta',
      confirmation: 'Explicit scope confirmation',
      close: 'Close',
      preview: 'Preview effects',
      saving: 'SAVING…',
      save: 'Save decisions — without confirming Shipment',
      CANCEL_BEFORE_DISPATCH: 'Cancelled before dispatch — no movements for either linked line',
      INVOICE_ONLY_NO_DISPATCH: 'Invoice error without dispatch — original line unlinked',
      DISPOSAL: 'Disposed at recipient — no warehouse receipt',
      RETURN_TO_STOCK: 'Physical return — receipt into source warehouse',
    },
    title: 'Official shipments',
    description: 'Upload, validate, confirm and roll back official shipment workbooks.',
    upload: 'Upload workbook',
    history: 'Import history',
    filename: 'Filename',
    shipmentDates: 'Shipment dates',
    status: 'Status',
    uploaded: 'Uploaded',
    destinations: 'Destinations',
    products: 'Products',
    lines: 'Shipment lines',
    total: 'Total quantity',
    ledger: 'Inventory ledger',
    pipeline: 'Network pipeline',
    preview: 'Preview and mapping validation',
    issues: 'Blocking issues',
    date: 'Date',
    destination: 'Destination',
    destinationType: 'Destination type',
    product: 'Product',
    warehouse: 'Source warehouse',
    warehouseRouteMissing: 'Warehouse route missing',
    warehouseSourceDestinationMatch: 'Source and destination Warehouse must differ',
    confirm: 'Confirm official shipment report',
    revalidate: 'Revalidate mappings',
    resolveStore: 'Add or link Store',
    rollback: 'Roll back report',
    rollbackReason: 'Mandatory rollback reason',
    detail: 'Shipment report detail',
    duplicate: 'This workbook duplicates existing shipment evidence and cannot be confirmed.',
    conflict:
      'An overlapping confirmed shipment requires reconciliation before it can be confirmed.',
    semantics:
      'A direct positive shipment increases the Store and decreases its source Warehouse. A warehouse delivery increases its destination Warehouse and decreases its source Warehouse. A central shipment decreases the Warehouse and posts pipeline only. Negative central lines are blocked.',
    choose: 'Choose an official shipment .xlsx workbook',
    back: 'Back to import history',
    download: 'Download original',
    unconfirmedDates: 'Dates become official only after confirmation',
    matchProduct: 'Match product',
    canonicalProduct: 'Canonical product',
    matchProductHelp:
      'The match stores a manual source alias. The report remains blocked until it is revalidated.',
    newProductUnit: 'New product unit',
    createProduct: 'Create new product',
    matchExistingProduct: 'Match existing product',
    leaveUnresolved: 'Leave unresolved',
    operationFailed: 'The shipment import operation could not be completed.',
    failure: {
      SHIPMENT_HEADER_INVALID: 'The Shipment file has an invalid header layout.',
      SHIPMENT_DATE_INVALID: 'The Shipment file contains an invalid date.',
      SHIPMENT_INVOICE_NUMBER_REQUIRED: 'A Shipment line is missing its invoice number.',
      SHIPMENT_INVOICE_NUMBER_UNSAFE_NUMERIC:
        'The invoice number was stored in Excel as an unsafe number and may have lost digits. Format the Invoice # column as Text and re-enter the original number.',
      SHIPMENT_INVOICE_NUMBER_INVALID:
        'The invoice number has an unsupported type or format. Format the Invoice # column as Text.',
      SHIPMENT_INVOICE_NUMBER_TOO_LONG:
        'The invoice number is too long. The maximum length is 128 characters.',
      SHIPMENT_QUANTITY_MISSING: 'A Shipment line is missing its quantity.',
      SHIPMENT_REQUIRED_VALUE_MISSING: 'A required value is missing from the Shipment file.',
      SHIPMENT_DESTINATION_INVALID: 'The Shipment file contains an invalid destination.',
      SHIPMENT_PRODUCT_INVALID: 'The Shipment file contains an invalid Product value.',
      SHIPMENT_INVALID_NUMERIC_CELL: 'The Shipment file contains an invalid quantity.',
      SHIPMENT_FORMULA_OR_RICH_CELL: 'Required Shipment fields must contain values, not formulas.',
      SHIPMENT_ZERO_QUANTITY: 'A Shipment line cannot have a zero quantity.',
      SHIPMENT_WORKSHEET_UNEXPECTED: 'The Shipment file must contain one Netto worksheet.',
      SHIPMENT_LINES_MISSING: 'The Shipment file contains no Shipment lines.',
      SHIPMENT_MALFORMED_XLSX: 'The Shipment workbook could not be read.',
      SHIPMENT_FILE_SIZE_INVALID: 'The Shipment file is empty or exceeds the allowed size.',
      SHIPMENT_NOT_XLSX: 'The Shipment source is not a valid XLSX file.',
      SHIPMENT_XLSX_LIMIT_EXCEEDED: 'The Shipment workbook exceeds the safe processing limits.',
      SHIPMENT_UNSAFE_XLSX_CONTENT: 'The Shipment workbook contains unsupported unsafe content.',
      SHIPMENT_STAGING_FAILED: 'The Shipment data could not be prepared for validation.',
      SHIPMENT_PARSE_FAILED: 'The Shipment file data could not be read.',
    },
  },
  production: {
    title: 'Production planning',
    planningOnly: 'Plan production, record actual facts, then post inventory only at completion.',
    product: 'Product',
    date: 'Production date',
    location: 'Location',
    planningUnit: 'Planning unit',
    plannedQuantity: 'Planned quantity',
    quantity: 'Quantity',
    status: 'Status',
    draft: 'Draft',
    planned: 'Planned',
    actions: 'Actions',
    openOrder: 'Open',
    loading: 'Loading production order…',
    close: 'Close',
    batchPending: 'Batch pending',
    plannedVsActual: 'Planned / actual output',
    actualsNoLedger: 'Actual facts are provisional. Inventory is posted only on completion.',
    actualOutput: 'Actual output',
    actualInputs: 'Actual inputs',
    actual: 'Actual',
    variance: 'Variance',
    batch: 'Batch',
    expiry: 'Use by',
    actualMaterial: 'Actual material',
    actualQuantity: 'Actual quantity',
    waste: 'Process waste',
    wasteCategory: 'Waste category',
    stage: 'Stage',
    machine: 'Machine/resource',
    stageStartedAt: 'Stage started',
    stageEndedAt: 'Stage ended',
    operationMinutes: 'Operation minutes',
    people: 'People',
    saveActuals: 'Save actuals',
    timingSummary: 'Elapsed: {{elapsed}} min · operation: {{operation}} min',
    start: 'Start',
    completeReview: 'Complete — post reviewed inventory effects',
    cancel: 'Cancel',
    reverse: 'Reverse',
    inventoryEffectsPosted: '{{count}} inventory effects posted',
    orderStatus: {
      DRAFT: 'Draft',
      PLANNED: 'Planned',
      IN_PROGRESS: 'In progress',
      COMPLETED: 'Completed',
      CANCELLED: 'Cancelled',
      REVERSED: 'Reversed',
    },
    savePlan: 'Save plan',
    saveFailed: 'Plan could not be saved.',
    readOnly: 'You have read-only access.',
    requirementsPreview: 'Requirements preview',
    finishedOutput: 'Finished output',
    requirement: 'Requirement',
    kind: 'Kind',
    rolls: 'Full rolls',
    weeklyRequirements: 'Weekly requirements',
    previousWeek: 'Previous week',
    currentWeek: 'Current week',
    nextWeek: 'Next week',
    noPlans: 'No plans in the selected week.',
    selectedProduct: 'the selected product',
    configurationHint:
      'To plan {{product}}, an administrator must configure a versioned recipe and packaging format. Available materials: {{materials}}.',
    configuration: 'Planning configuration',
    configurationWorkspaceHelp:
      'Configure recipes, packaging, film and production stages in the single costing workspace. The planner stays simple.',
    openMaterials: 'Open materials',
    openProductConfiguration: 'Configure product',
    readyToPlan: 'READY TO PLAN',
    configurationMissing: 'CONFIGURATION MISSING',
    configurationChecklist: {
      recipe: 'Recipe',
      output: 'Output / yield',
      packaging: 'Packaging',
      multivac: 'Multivac',
      stages: 'Production stages',
    },
    noConfiguredStages: 'No timed production stages are configured.',
    newMaterial: 'New material',
    materialCode: 'Material code',
    materialName: 'Material name',
    rollLengthMetres: 'Roll length (m)',
    stockManaged: 'Stock managed',
    costManaged: 'Cost managed',
    unit: 'Unit',
    createMaterial: 'Add material',
    recipeVersion: 'Recipe version',
    referenceOutput: 'Reference output',
    ingredient: 'Ingredient',
    ingredientQuantity: 'Ingredient quantity',
    createRecipe: 'Add recipe',
    formatVersion: 'Packaging format',
    finishedPerUnit: 'Finished output per unit',
    finishedUnit: 'Finished unit',
    packsPerUnit: 'Packs per unit',
    packagingMaterial: 'Packaging material',
    packagingQuantity: 'Packaging quantity',
    packsPerCycle: 'Packs per cycle',
    pitchMetres: 'Machine pitch (m)',
    lowerFilm: 'Lower film',
    upperFilm: 'Upper film',
    createFormat: 'Add format',
    chooseProductFirst: 'choose a product first',
    missing: {
      PRODUCTION_FORMAT_REQUIRED:
        'No active packaging format is configured for this unit and date.',
      PRODUCTION_RECIPE_REQUIRED: 'No active recipe is configured for this product and date.',
      PRODUCTION_RECIPE_OUTPUT_UNIT_MISMATCH:
        'Recipe output unit does not match the format. Configure an explicit relationship.',
      PRODUCTION_FILM_CONFIGURATION_REQUIRED:
        'Film configuration requires packs per cycle and machine pitch.',
      PRODUCTION_FILM_ROLL_LENGTH_REQUIRED: 'The selected film requires a configured roll length.',
      generic: 'Required production configuration is missing.',
    },
    requirementKinds: {
      INGREDIENT: 'Ingredient',
      PACKAGING: 'Packaging',
      LOWER_FILM: 'Lower film',
      UPPER_FILM: 'Upper film',
    },
  },
  dashboard: {
    title: 'Operational dashboard',
    description: 'Read-only currentness, Sales and Stock attention.',
    asOf: 'As of',
    network: 'Network',
    allNetworks: 'All networks',
    store: 'Store',
    allStores: 'All stores',
    loadFailed: 'Dashboard could not be loaded.',
    dataStatus: 'Data status',
    latestSales: 'Latest confirmed Sales: {{date}}',
    coverage: '28-day coverage: {{status}}',
    missingSalesDays: 'Missing Sales days: {{count}}',
    stockIncomplete: 'Stock incomplete: {{count}}',
    stockNotInitialized: 'Stock not initialized: {{count}}',
    latestSalesImport: 'Latest Sales import: {{date}}',
    latestShipmentImport: 'Latest Shipment import: {{date}}',
    importsActionRequired: 'Imports requiring action: {{count}}',
    importsMissing: 'Missing reports: {{count}} (Sales {{sales}}, Shipments {{shipments}})',
    oldestImportProblem: 'Oldest import issue: {{date}}',
    salesSnapshot: 'Sales snapshot',
    period: 'Period',
    knownQuantity: 'Known quantity',
    coverageLabel: 'Coverage',
    activeScope: 'Active scope',
    storesProducts: '{{stores}} stores · {{products}} products',
    noSalesAccess: 'Sales details are not available for this user.',
    stockSummary: 'Stock attention summary',
    lowStock: 'Low / reorder: {{count}}',
    stockNotManaged: 'Centrally managed: {{count}}',
    noStockAccess: 'Stock details are not available for this user.',
    salesTrend: 'Known Sales trend',
    salesTrendDescription:
      'Each marker is a daily known quantity. ? is a missing whole Sales day, never zero.',
    stockAttention: 'Stock attention',
    product: 'Product',
    current: 'Current',
    goal: 'GOAL',
    difference: 'Difference',
    reorder: 'Reorder',
    state: 'State',
    notNumeric: 'UNKNOWN',
    provisional: 'provisional',
    noStockAttention: 'No direct-store attention items for this scope.',
    topProducts: 'Top products by known Sales',
    storesCount: '{{count}} stores',
    dataIssues: 'Data issues',
    noDataIssues: 'No current data issues.',
    recentImports: 'Recent import activity',
    coverageState: { COMPLETE: 'COMPLETE', INCOMPLETE_DATA: 'INCOMPLETE DATA' },
    periods: {
      LATEST_CONFIRMED_DAY: 'Latest confirmed day',
      LAST_7_DAYS: 'Last 7 days',
      LAST_28_DAYS: 'Last 28 days',
    },
    issues: {
      MISSING_SALES_DAYS: '{{count}} missing Sales report days',
      STOCK_INCOMPLETE_DATA: '{{count}} Stock cells with incomplete data',
      STOCK_NOT_INITIALIZED: '{{count}} Stock cells not initialized',
      IMPORT_REVIEW_REQUIRED: '{{count}} import batches require review',
    },
  },
  operations: {
    title: 'Operations',
    previous: 'Previous week',
    current: 'Current week',
    next: 'Next week',
    loadFailed: 'Operations could not be loaded.',
    brief: 'Weekly brief',
    briefText:
      'This week: {{planned}} planned, {{inProgress}} in progress, {{completed}} completed. Missing Sales days: {{missingSalesDays}}. Shipment owner decisions: {{shipmentOwnerDecisions}}.',
    production: 'Production this week',
    openProduction: 'Open Production',
    openWarehouse: 'Open Warehouse',
    storeOrders: 'Store Orders',
    openStoreOrders: 'Open Store Orders',
    storeOrdersCoverage: '{{confirmed}} / {{expected}} confirmed Stores · {{missing}} missing',
    forecast: 'Nearest-week forecast',
    openForecast: 'Open Forecast',
    forecastUnavailable: 'No forecast run is available yet.',
    lowConfidence: '{{count}} low-confidence row(s)',
    date: 'Date',
    product: 'Product',
    quantity: 'Quantity',
    location: 'Location',
    statusLabel: 'Status',
    moveDepartment: 'Move to department',
    noOrders: 'No Production orders this week.',
    requirements: 'Weekly requirements',
    requirement: 'Requirement',
    kind: 'Kind',
    rolls: 'Rolls',
    noRequirements: 'No planned requirements.',
    attention: 'Needs attention',
    open: 'Open',
    noIssues: 'No current operational issues.',
    issue: {
      missingSalesCoverage: '{{count}} missing Sales report day(s).',
      shipmentUnconfirmed: 'A Shipment report remains unconfirmed.',
      shipmentProductIdentities: '{{count}} Shipment Product identity decision(s) unresolved.',
      shipmentWarehouseRoutes: '{{count}} Shipment source Warehouse route decision(s) unresolved.',
    },
    status: {
      DRAFT: 'Draft',
      PLANNED: 'Planned',
      IN_PROGRESS: 'In progress',
      COMPLETED: 'Completed',
      CANCELLED: 'Cancelled',
      REVERSED: 'Reversed',
    },
  },
  forecast: {
    title: 'Forecast',
    model: 'Model {{version}} · Sales through {{through}}',
    noRun: 'No forecast run is available yet.',
    noRunEditor:
      'No forecast run is available yet. Recalculate from authoritative Sales when ready.',
    horizon: { 7: 'Next 7 days', 14: 'Next 14 days', 28: 'Next 28 days' },
    recalculate: 'Recalculate Forecast',
    recalculateFailed: 'Forecast recalculation failed.',
    addPromotion: 'Add promotion',
    editPromotion: 'Edit promotion',
    productSearch: 'Product search',
    product: 'Product',
    store: 'Store',
    network: 'Network',
    all: 'All',
    date: 'Date',
    baseline: 'Baseline',
    promotion: 'Promotion',
    final: 'Final forecast',
    confidence: 'Confidence',
    confidenceValue: { HIGH: 'High', MEDIUM: 'Medium', LOW: 'Low', NO_DATA: 'No data' },
    manual: 'Manual',
    manualOverride: 'Manual forecast override',
    overrideQuantity: 'Final quantity ({{unit}})',
    reason: 'Reason',
    saveOverride: 'Save override',
    explain: 'Explain',
    drivers: 'Drivers',
    close: 'Close',
    scope: 'Scope',
    from: 'From',
    to: 'To',
    uplift: 'Manual uplift (%)',
    description: 'Description',
    save: 'Save',
    promotions: 'Promotions',
    noPromotions: 'No promotions.',
    noUplift: 'No manual uplift',
    noRows: 'No forecast rows match the selected filters.',
    noData: 'No data',
    loadFailed: 'Forecast could not be loaded.',
  },
  costing: {
    recipeImportAction: 'Import recipe',
    recipeImportTitle: 'Import recipe from document',
    recipeImportHelp:
      'We will read ingredients for {{product}}. Existing materials will be reused and missing ones created without a price.',
    recipeImportChooseFile: 'Choose a DOCX or PDF file',
    recipeImportFileTooLarge: 'The file is larger than 8 MB.',
    recipeImportReading: 'Reading…',
    recipeImportPreview: 'Read and preview',
    recipeImportNotReady: 'A valid preview is required before import.',
    recipeImportOutputRequired: 'Enter a positive finished recipe output.',
    recipeImportExistingCount: 'Matched existing: {{count}}',
    recipeImportNewCount: 'New materials: {{count}}',
    recipeImportMissingPrices:
      'New materials will be saved without a price. The calculation remains partial until their costs are entered.',
    recipeImportOutputQuantity: 'Finished recipe output',
    recipeImportOutputUnit: 'Output unit',
    recipeImportOutputHelp: 'Enter the actual finished-product quantity produced by this recipe.',
    recipeImportSaving: 'Saving…',
    recipeImportSave: 'Create materials and save recipe',
    recipeImportStatus: {
      MATCHED: 'Existing',
      NEW: 'New, no price',
      UNIT_CONFLICT: 'Unit conflict',
      AMBIGUOUS_MATCH: 'Ambiguous',
    },
    recipeImportWarnings: {
      PDF_TEXT_UNAVAILABLE: 'The PDF has no extractable text.',
      DOCX_DOCUMENT_XML_MISSING: 'The DOCX document structure could not be read.',
      NO_RECIPE_INGREDIENTS_DETECTED: 'No ingredient and quantity table was detected.',
      UNSUPPORTED_RECIPE_FILE_TYPE: 'DOCX and text-based PDF files are supported.',
    },
    title: 'Product cost calculator',
    subtitle: 'Universal standard, actual batch and R&D economics.',
    mode: 'Mode',
    standard: 'Standard',
    actual: 'Actual batch',
    draft: 'Draft / R&D',
    date: 'Calculation date',
    product: 'Product',
    batch: 'Completed batch',
    select: 'Select',
    draftName: 'Draft name',
    outputQuantity: 'Output quantity',
    output: 'Output',
    unit: 'Unit',
    material: 'Material',
    temporaryMaterial: 'Temporary material',
    quantity: 'Quantity',
    unitCost: 'Unit cost',
    addMaterial: 'Add material / packaging',
    addAndUse: 'Add to base and use',
    useOnlyThisCalculation: 'Use only in this calculation',
    materialKind: 'Type',
    ingredient: 'Ingredient',
    packaging: 'Packaging',
    explicitConsumable: 'Explicit consumable',
    stockManaged: 'Stock managed',
    lowerFilm: 'Lower film',
    upperFilm: 'Upper film',
    labor: 'Labor',
    machine: 'Machine/resource',
    addLine: 'Add cost component',
    removeLine: 'Remove',
    cancel: 'Cancel',
    component: 'Component',
    componentType: 'Component type',
    total: 'Total',
    state: 'State',
    summary: 'Economics',
    batchCost: 'Batch cost',
    prices: 'Candidate net selling prices',
    pricesHint: 'Separate prices with commas',
    targetMargin: 'Target margin (%)',
    targetPrice: 'Required net price',
    whatIf: 'What-if cost change (%)',
    scenarioCost: 'Scenario batch cost',
    sellingPrice: 'Net selling price',
    profit: 'Profit / unit',
    margin: 'Margin',
    markup: 'Markup',
    batchProfit: 'Batch profit',
    calculationUnavailable: 'A required configuration or cost input is unavailable.',
    notCalculated: 'Not calculated',
    notStarted: 'Not started',
    materials: 'Materials / recipe',
    production: 'Production',
    materialsDescription: 'Ingredients and recipe inputs for this calculation.',
    productionDescription: 'Recorded or scenario operational time, labor and machine cost.',
    packagingDescription: 'Packaging, lower film, upper film and explicit consumables.',
    sectionNavigation: 'Cost calculation sections',
    stage: 'Stage',
    operatingTime: 'Operating time',
    provenance: 'Cost basis',
    noComponents: 'No components in this section.',
    noOperationalStages: 'No operational stages are available.',
    addIngredient: 'Add ingredient',
    addPackaging: 'Add packaging',
    addStage: 'Add stage',
    recipeStagesHelp:
      'Optional timed stages use the effective labor and machine rates in the complete batch cost.',
    stageCode: 'Stage code',
    minutes: 'Minutes',
    people: 'People',
    materialsSubtotal: 'Materials subtotal',
    productionSubtotal: 'Production subtotal',
    packagingSubtotal: 'Packaging subtotal',
    draftIdentity: 'Local R&D scenario — no operational evidence is changed.',
    standardIdentity: 'Resolved standard recipe and effective cost basis.',
    actualIdentity: 'Completed batch facts and historical effective cost basis.',
    currency: 'Currency',
    costSummary: 'Batch cost summary',
    incompleteCost: 'The known amount is shown, but one or more required costs are missing.',
    unitCostCard: 'Unit cost',
    perUnit: 'Per 1 {{unit}}',
    authoritativeUnitsOnly: 'Only explicitly convertible authoritative units are shown.',
    sellingEconomics: 'Selling price and margin',
    addPrice: 'Add price',
    priceUnit: 'Net ISK per {{unit}}',
    calculatePrice: 'Calculate price',
    scenarios: 'Scenarios (what-if)',
    scenariosDescription: 'Local comparison only; scenarios do not persist or post evidence.',
    scenario: 'Scenario',
    baselineScenario: 'SCEN 1 — BASELINE',
    whatIfScenario: 'WHAT-IF',
    costChange: 'Cost change: {{percent}}%',
    totalCost: 'Unit total cost',
    batchRevenue: 'Batch revenue',
    priceComparison: 'Candidate price comparison',
    libraryTitle: 'Product cost library',
    libraryDescription:
      'Persistent, versioned product cost models. Saving does not post inventory.',
    searchModels: 'Search products and models',
    department: 'Department',
    allDepartments: 'All departments',
    productOtherDepartment: 'PRODUCT BELONGS TO ANOTHER DEPARTMENT',
    favorites: 'Favorites',
    favorite: 'Favorite',
    rnd: 'R&D',
    noSavedVersion: 'No saved version',
    noSavedModels: 'No saved cost models match the filters.',
    selectSavedModel: 'Select a saved model to reopen its calculation and history.',
    versionHistory: 'Immutable version history',
    immutableSnapshots:
      'Saved versions are immutable evidence; recalculate creates a new version using current effective costs.',
    reopen: 'Reopen',
    recalculate: 'Recalculate',
    useAsTemplate: 'Use as template',
    copy: 'copy',
    saveDraft: 'Save draft',
    saveNewVersion: 'Save new version',
    newProductModel: 'Product cost model',
    approveStandardCost: 'Approve as standard cost',
    approvalHelp:
      'This explicitly creates the effective-dated Product standard cost used by profitability. It does not post inventory.',
    effectiveFrom: 'Effective from',
    reason: 'Reason',
    approve: 'Approve',
    costingChecklist: 'Costing checklist',
    versionComparison: 'Current {{current}} · previous {{previous}}',
    productLibrary: 'Products',
    costInputBase: 'Cost input base',
    excludedProducts: 'Excluded',
    excludeFromCosting: 'Exclude from costing',
    restoreCosting: 'Restore to costing',
    openProductEditor: 'Open editor',
    unassignedDepartment: 'Unassigned',
    code: 'Code',
    currentCost: 'Current cost',
    costHistory: 'Cost history',
    actions: 'Actions',
    setCost: 'Set cost',
    setRate: 'Set rate',
    rateAmount: 'Rate',
    saveRate: 'Save rate',
    costNotManaged: 'Cost not managed',
    complete: 'Complete',
    missingCost: 'Missing cost',
    hour: 'hour',
    noRates: 'No active rates.',
    purchaseAmount: 'Purchase amount',
    purchaseQuantity: 'Purchase quantity',
    derivedUnitCost: 'The exact unit cost is calculated from this purchase.',
    sourceReference: 'Source / reference',
    fxRate: 'FX rate to ISK',
    saveCost: 'Save cost',
    filmRollCostHelp: 'Roll price is divided by its length: {{length}}.',
    requiredMetres: 'Required: {{metres}}',
    practicalRolls: 'practical rolls: {{count}} × {{length}}',
    rollLength: 'Roll length (m)',
    canonicalConfiguration: 'Production configuration',
    canonicalConfigurationHelp:
      'Creates only a new version of the existing Production Recipe or Format.',
    recipeLines: 'Recipe lines',
    saveRecipeVersion: 'Save recipe version',
    planningUnit: 'Planning unit',
    packsPerPlanningUnit: 'Packs per planning unit',
    packagingLines: 'Packaging lines',
    filmMultivac: 'Film / Multivac',
    packsPerCycle: 'Packs per cycle',
    pitchMetres: 'Pitch (metres)',
    filmConfigurationRequired: 'Film requires packs per cycle and pitch.',
    saveFormatVersion: 'Save format version',
    standardShort: 'STD',
    archive: 'Archive',
    materialsMasterTitle: 'Materials & ingredients',
    materialsMasterDescription: 'One shared material base for recipes, packaging and costing.',
    searchMaterials: 'Search code or name',
    newMaterial: 'New material',
    materialCode: 'Material code',
    materialName: 'Material name',
    materialCodeHelp: 'Internal / ERP / supplier code, e.g. 51465',
    materialNameHelp: 'Name used in recipes, e.g. Nauta Hakkefni',
    savingMaterial: 'SAVING...',
    materialCreateSuccess: 'Material added.',
    materialCreateErrors: {
      duplicateCode: 'A material with code {{code}} already exists.',
      invalidCategoryOrUnit: 'Choose a valid material type and unit.',
      permissionDenied: 'You do not have permission to add materials.',
      validationFailed: 'Check the required fields and data values.',
      saveFailed: 'Material could not be saved. Please try again.',
    },
    allMaterials: 'All',
    noMaterials: 'No materials match the filter.',
    usedBy: 'Used by',
    active: 'Active',
    archived: 'Archived',
    yes: 'Yes',
    no: 'No',
    close: 'Close',
    save: 'Save',
    costManaged: 'Include in cost calculation',
    trackStock: 'Track warehouse stock',
    includeCost: 'Include in cost calculation',
    materialNoCostHelp:
      'A material not managed for cost needs no price and does not make costing incomplete until it is used as a cost input.',
    rollLengthHelp: 'Length of one roll in metres. Roll price is normalized per metre.',
    noCostHistory: 'No saved cost history.',
    loadFailed: 'Materials could not be loaded.',
    materialCategory: {
      RAW_MATERIAL: 'Raw material',
      ADDITIVE: 'Additive',
      PACKAGING: 'Packaging',
      FILM: 'Film',
      UNCLASSIFIED: 'Unclassified',
    },
    productionCostWorkspace: 'Production costing',
    productionCostWorkspaceDescription:
      'Complete batch economics from raw materials to selling margin. All saved versions retain their price snapshot.',
    calculator: 'Calculator',
    calculationHeader: 'Calculation header',
    calculationHeaderHelp: 'Identity, batch basis and commercial context for this version.',
    standardCalculation: 'Standard',
    researchCalculation: 'R&D',
    finishedProduct: 'Finished product',
    recipe: 'Recipe',
    recipeVersion: 'Recipe v{{version}}',
    noActiveRecipe: 'No active recipe',
    calculationName: 'Calculation name',
    newCalculation: 'New calculation',
    variant: 'Product variant',
    inputBatchKg: 'Input batch (kg)',
    pieceWeightGrams: 'Piece weight (g)',
    piecesPerCarton: 'Pieces per carton',
    customerOrNetwork: 'Customer / network',
    noSpecificCustomer: 'No specific customer',
    searchProduct: 'Search by code, name or alias',
    searchMaterial: 'Search by code or name',
    recentlyUsed: 'recent',
    activeRecipe: 'active recipe',
    openProductCard: 'Open Product card',
    noCurrentPrice: 'No current price',
    historicalPrice: 'Historical price',
    priceEffectiveOn: 'Price effective {{date}}',
    abpPrice: 'ABP price',
    abpPriceEffectiveOn: 'ABP · {{date}}',
    catalogGroup: {
      rawMaterials: 'Local raw materials',
      additives: 'Additives',
      packaging: 'Packaging',
      abp: 'ABP raw materials',
      manual: 'Manual entry',
      networks: 'Networks',
      stores: 'Stores',
    },
    note: 'Note',
    additionalSettings: 'Additional settings',
    additionalSettingsHelp: 'Optional name, variant, piece weight and note.',
    loadCanonicalRecipe: 'Load current canonical recipe',
    recipeCalculationHelp: 'Enter ingredient kilograms or exact percentage shares.',
    chooseProductOrAddIngredient: 'Choose a product or add the first ingredient',
    missingCostData: 'Missing cost data',
    customerPrice: 'Customer price',
    recipePercent: 'Recipe in percentages',
    recipeKg: 'Recipe in kilograms',
    manualEntry: 'Manual scenario item',
    manualOverride: 'Manual override',
    lossAndYield: 'Production losses and yield',
    lossAndYieldHelp: 'Loss stages are applied sequentially and never counted twice.',
    addLossStage: 'Add loss stage',
    lossMode: 'Loss unit',
    lossValue: 'Loss',
    massAfterStage: 'Mass after stage',
    laborOperations: 'Operations and labor',
    laborOperationsHelp: 'Employees × hours × loaded hourly cost.',
    addOperation: 'Add operation',
    operation: 'Operation',
    hours: 'Hours',
    hourlyLoadedCost: 'Loaded hourly cost',
    machinesAndEnergy: 'Machines and energy',
    machinesAndEnergyHelp: 'Machine time remains separate from labor.',
    addMachine: 'Add machine',
    hourlyCost: 'Hourly cost',
    packagingMaterials: 'Packaging materials',
    packagingMaterialsHelp: 'Usage is calculated per piece, carton or batch, including waste.',
    calculationBasis: 'Calculation basis',
    consumption: 'Consumption',
    wastePercent: 'Material waste (%)',
    perPiece: 'Per piece',
    perCarton: 'Per carton',
    perBatch: 'Per batch',
    explicitOtherCosts: 'Other explicit costs',
    explicitOtherCostsHelp:
      'Quality, cleaning, storage, logistics, energy and waste remain visible.',
    addExplicitCost: 'Add explicit cost',
    category: 'Category',
    name: 'Name',
    rate: 'Rate',
    otherCategory: {
      QUALITY_CONTROL: 'Quality control',
      CLEANING_SANITATION: 'Cleaning and sanitation',
      STORAGE: 'Storage',
      INTERNAL_LOGISTICS: 'Internal logistics',
      ADDITIONAL_ENERGY: 'Additional energy',
      WASTE_DISPOSAL: 'Waste disposal',
    },
    costBasis: {
      PER_BATCH: 'Per batch',
      PER_KG: 'Per kg',
      PER_HOUR: 'Per hour',
      PER_PALLET: 'Per pallet',
    },
    netPriceHelp: 'All selling prices and margins are net of tax.',
    priceUnitLabel: 'Selling price unit',
    piece: 'piece',
    carton: 'carton',
    distributionCost: 'Distribution cost / batch',
    discountPercent: 'Discount (%)',
    finishedMass: 'Finished mass',
    yield: 'Yield',
    fullPieces: 'Full pieces',
    fullCartons: 'Full cartons',
    costPerKg: 'Cost / kg',
    costPerPiece: 'Cost / piece',
    costPerCarton: 'Cost / carton',
    unsavedChanges: 'Unsaved changes',
    discardUnsavedChanges: 'Discard the unsaved calculation changes?',
    archiveCalculationConfirm: 'Archive this calculation? Its immutable history will remain.',
    savingCalculation: 'Saving…',
    calculationSaved: 'Calculation version saved.',
    calculating: 'Calculating…',
    fixValidationErrors: 'Correct the marked calculation errors before saving.',
    missingUsedCosts: '{{count}} used cost input(s) have no authoritative price.',
    completeness: { COMPLETE: 'Complete', PARTIAL: 'Partial', UNKNOWN_COST: 'Unknown cost' },
    validation: {
      INPUT_BATCH_MUST_BE_POSITIVE: 'Input batch must be greater than zero.',
      RECIPE_PERCENT_MUST_EQUAL_100: 'Recipe shares must total exactly 100%.',
      LOSS_PERCENT_EXCEEDS_100: 'A percentage loss cannot exceed 100%.',
      LOSS_EXCEEDS_AVAILABLE_MASS: 'A loss cannot exceed the mass available at that stage.',
      PIECE_WEIGHT_MUST_BE_POSITIVE: 'Piece weight must be greater than zero.',
      PACKAGING_CONVERSION_REQUIRED: 'Piece/carton packaging requires explicit piece conversion.',
      SELLING_UNIT_CONVERSION_REQUIRED: 'The selected selling unit requires explicit conversion.',
      DISCOUNT_EXCEEDS_100: 'Discount cannot exceed 100%.',
      TARGET_MARGIN_MUST_BE_BELOW_100: 'Target margin must be below 100%.',
      VALUE_MUST_BE_NON_NEGATIVE: 'Value cannot be negative.',
    },
    recipePercentTotal: 'Recipe total',
    all: 'All',
    approved: 'Approved',
    sort: 'Sort',
    sortUpdated: 'Recently updated',
    sortCalculationDate: 'Calculation date (newest)',
    sortName: 'Name',
    sortCostAsc: 'Unit cost ascending',
    sortCostDesc: 'Unit cost descending',
    sortMarginAsc: 'Margin ascending',
    sortMarginDesc: 'Margin descending',
    dateFrom: 'Calculation date from',
    dateTo: 'Calculation date to',
    comparisonDifference: 'Difference',
    comparisonDifferencePercent: 'Difference %',
    laborTime: 'Labor time',
    defaultApprovalReason: 'Approved production cost snapshot',
    departments: 'departments',
    calculations: 'calculations',
    costBaseTruthHelp:
      'Effective-dated Materials, labor and machine rates are shared with Production; this screen creates no duplicate master data.',
    openMaterials: 'Open Material master',
    version: 'Version',
    compareVersions: 'Compare saved versions',
    scenarioA: 'Scenario A',
    scenarioB: 'Scenario B',
    metric: 'Metric',
    restore: 'Restore',
  },
  orders: {
    title: 'Store Orders',
    description:
      'Explicit Store demand by delivery round. No Forecast or stock subtraction is applied.',
    entry: 'ORDER ENTRY',
    summary: 'SUMMARY',
    submitted: 'SUBMITTED ORDERS',
    newRound: 'New round',
    closeRound: 'Close round',
    round: 'Delivery round',
    deliveryDate: 'Delivery date',
    reference: 'Reference',
    expectedStores: 'Expected Stores',
    storesInOrder: 'Stores in order',
    addStore: 'Add Store',
    addStoreButton: 'Add',
    addingStore: 'ADDING...',
    removeStore: 'Remove Store',
    removingStore: 'REMOVING...',
    removeStoreConfirm: 'Remove {{store}} from this round?',
    removeDraftStoreConfirm:
      'This Store has a saved draft. Cancel the current draft and remove the Store from the round?',
    cancelDraftAndRemove: 'Cancel draft and remove',
    confirmedStoreRemoveBlocked: 'This Store has a confirmed order. Cancel the order first.',
    removeConfirmedStoreConfirm:
      'Cancel the confirmed order for {{store}} and remove the Store from this round?',
    cancelConfirmedOrderAndRemove: 'Cancel order and remove Store',
    confirmedOrderCancelledAndStoreRemoved: 'Order cancelled. Store removed from the round.',
    roundUpdateErrors: {
      staleRevision: 'The round changed. Data was refreshed — try again.',
      invalidStore: 'The selected Store cannot be added.',
      roundNotEditable: 'A closed round cannot be modified.',
      activeStoreOrders: 'A Store with an active order cannot be removed from the round.',
      confirmedStoreLocked: 'This Store has a confirmed order. Cancel the order first.',
      addFailed: 'The Store could not be added.',
      removeFailed: 'The Store could not be removed.',
    },
    searchStore: 'Search Stores',
    selectStore: 'Select a Store to enter an order.',
    noRound: 'No active Store Order round.',
    noRoundForWeek:
      'No Store Order round exists in this week. Closed historical weeks remain available when selected.',
    weekHeading: 'WEEK {{week}} · {{from}}–{{to}}',
    previousWeek: 'Previous week',
    currentWeek: 'Current week',
    nextWeek: 'Next week',
    department: 'Department',
    allDepartments: 'All departments',
    network: 'Network',
    allNetworks: 'All networks',
    addProduct: 'Add product',
    removeProduct: 'Remove product',
    orderEditor: 'Order editor',
    quantity: 'Quantity',
    note: 'Note',
    saveDraft: 'Save draft',
    confirm: 'Confirm order',
    confirmedStores: 'Confirmed Stores',
    missingStores: 'Missing Stores',
    product: 'Product',
    ordered: 'Ordered',
    planned: 'Already planned',
    remaining: 'TO PLAN',
    statusLabel: 'Status',
    productionWarehouse: 'Production warehouse',
    createProduction: 'Create Production draft',
    ready: 'Ready',
    productionConfigured: 'Production configured',
    pickingRequired: 'Picking required',
    overplanned: 'Overplanned',
    resetDepartmentOrder: 'Reset department order',
    resetProductOrder: 'Reset product order',
    storesList: 'Store list',
    productMatrix: 'Product matrix',
    store: 'Store',
    version: 'Version',
    confirmedAt: 'Confirmed at',
    show: 'Show',
    history: 'Version history',
    previous: 'Previous Store',
    next: 'Next Store',
    create: 'Create',
    cancel: 'Cancel',
    loading: 'Loading…',
    notReceived: 'Not received',
    importOrderFile: 'Import order file',
    fileImportReview: 'File import review',
    pdfTextUnavailable: 'This PDF does not contain text for automatic import.',
    storeMatches: 'STORE MATCHES',
    possibleStoreMismatch: 'POSSIBLE STORE MISMATCH: detected {{store}}.',
    fileImportFailed: 'The file could not be read. You can enter the order manually.',
    selectedStore: 'Selected Store: {{store}}',
    readFromImage: 'Read from image',
    imageReview: 'Image extraction review',
    imageReadFailed: 'The image could not be read. You can enter the order manually.',
    retryImageRead: 'Try again',
    enterManually: 'Enter manually',
    reviewRequired: 'Review required',
    addToDraft: 'Add to order draft',
    sources: 'Sources',
    removeLine: 'Remove line',
    rememberImageAlias: 'Remember alias',
    imageState: {
      READY: 'READY',
      REVIEW: 'REVIEW',
      UNMATCHED: 'UNMATCHED',
      INVALID_QUANTITY: 'INVALID QUANTITY',
      UNIT_REVIEW: 'UNIT REVIEW',
    },
    status: { NOT_RECEIVED: 'NOT RECEIVED', DRAFT: 'DRAFT', CONFIRMED: 'CONFIRMED' },
  },
  abpCatalog: {
    title: 'ABP Product Catalog',
    subtitle: 'Read-only ABP products, current purchase prices and last reliable landed cost.',
    syncNow: 'Sync now',
    lastSync: 'Last successful sync: {{date}}',
    neverSynced: 'Never synchronized',
    productCount: '{{count}} products',
    syncError:
      'The last synchronization failed ({{code}}). The last valid catalog remains available.',
    notConfigured: 'ABP synchronization is not configured. The existing cache remains read only.',
    manualSyncFailed: 'Manual synchronization failed. No cached products were removed.',
    search: 'Name, alias or PLU',
    freshFrozen: 'Fresh / Frozen',
    all: 'All',
    family: 'Family',
    visibility: 'Visibility',
    priceSource: 'Price source',
    dataCompleteness: 'Missing data',
    sort: 'Sort',
    product: 'Product',
    plu: 'PLU',
    familyVariant: 'Family / variant',
    priceEur: 'ABP EUR/kg',
    finalCost: 'Final ISK/kg',
    dataStatus: 'Data status',
    details: 'Details',
    noResults: 'No catalog products match the selected filters.',
    loadFailed: 'The ABP catalog could not be loaded.',
    officialName: 'Official name',
    abpId: 'ABP identifier',
    hsCode: 'HS code',
    importMode: 'Import method',
    costBasis: 'Cost basis',
    warnings: 'Warnings',
    none: 'None',
    close: 'Close',
    sync: {
      current: 'Current',
      stale: 'Out of date',
      error: 'Sync error',
      not_configured: 'Not configured',
      empty: 'Empty cache',
      syncing: 'Synchronizing',
    },
    source: {
      pricebook: 'Active pricebook',
      invoiceFallback: 'Latest invoice fallback',
      missing: 'No price',
    },
    status: {
      available: 'Available',
      unavailable: 'Unavailable',
      hidden: 'Hidden',
      review: 'Review',
      complete: 'Complete',
    },
    visibilityOption: {
      all: 'All',
      active: 'Active',
      hidden: 'Hidden',
      unavailable: 'Unavailable',
    },
    missingOption: {
      any: 'Any',
      price: 'Missing price',
      cost: 'Missing cost',
      none: 'Complete price and cost',
    },
    sortOption: {
      name: 'Name',
      plu: 'PLU',
      price_eur: 'EUR price',
      cost_isk: 'ISK cost',
      updated_at: 'Updated',
    },
    direction: { asc: 'Ascending', desc: 'Descending' },
    import: { tariff: 'Tariff', quota: 'Quota', exempt: 'Exempt / 0%' },
    review: {
      missing_price: 'missing price',
      ambiguous_price_source: 'ambiguous price source',
      missing_family: 'missing family',
      missing_variant: 'missing variant',
      missing_fresh_frozen: 'missing Fresh/Frozen',
      missing_hs_code: 'missing HS code',
      missing_import_mode: 'missing import method',
      missing_final_cost: 'missing final cost',
    },
  },
  profitability: {
    title: 'Profitability',
    subtitle:
      'Read-only net Sales profitability from confirmed Sales and effective historical costs.',
    readOnly: 'Read only',
    from: 'From',
    to: 'To',
    all: 'All',
    product: 'Product',
    store: 'Store',
    network: 'Network',
    products: 'Products',
    stores: 'Stores',
    networks: 'Networks',
    view: 'View',
    sort: 'Sort',
    direction: 'Direction',
    descending: 'Descending',
    ascending: 'Ascending',
    quantity: 'Quantity',
    revenue: 'Net revenue',
    cost: 'Cost',
    profit: 'Profit',
    margin: 'Margin',
    completeness: 'Cost completeness',
    date: 'Date',
    overTime: 'Profitability over time',
    noRows: 'No confirmed Sales match the selected filters.',
    loadFailed: 'Profitability could not be loaded.',
    costProvenance: 'Cost provenance',
    standardOnly:
      'Standard effective-dated cost is used. Actual batch cost is never guessed without an authoritative sale-to-batch relationship.',
    costVersionCount_one: '{{count}} standard cost version',
    costVersionCount_other: '{{count}} standard cost versions',
    state: { COMPLETE: 'Complete', PARTIAL: 'Partial', UNKNOWN_COST: 'Unknown cost' },
    basis: {
      STANDARD_EFFECTIVE: 'Effective standard cost',
      NO_AUTHORITATIVE_COST: 'No authoritative cost',
    },
  },
} satisfies LocaleResources;
